import React, { useState, useEffect, useCallback, useRef } from 'react';
import LoginPage from './components/LoginPage';
import ImportPaymentsModal from './components/ImportPaymentsModal';
import ImportProxyModal from './components/ImportProxyModal';
import ExportModal from './components/ExportModal';
import PaymentDetailModal from './components/PaymentDetailModal';
import { useStudents } from './hooks/useStudents';
import { useCalendar, countLessonsForStudent } from './hooks/useCalendar';
import { getCoachColorSettings, COLOR_MAP } from './utils/calendarColors';

const today = new Date();
const KV_API = 'https://mustgym-api.mustraininggym.workers.dev';
async function kvGet(key) {
  try {
    const res = await fetch(`${KV_API}/${key}`);
    if (!res.ok) return null;
    return await res.json();
  } catch(e) { return null; }
}
async function kvPut(key, data) {
  try {
    await fetch(`${KV_API}/${key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  } catch(e) { console.error('KV write failed', e); }
}

export default function App() {
  // ★ 修正重複登入 Bug：KV sync 觸發 reload 後，sessionStorage 仍保有 token
  // sessionStorage 的生命週期與分頁相同，關閉分頁或登出後才清除，符合 OAuth token 的安全需求
  const [accessToken, setAccessToken] = useState(() => {
    return sessionStorage.getItem('gAccessToken') || null;
  });

  // 每次 accessToken 更新時同步到 sessionStorage
  const handleSetAccessToken = useCallback((token) => {
    if (token) {
      sessionStorage.setItem('gAccessToken', token);
    } else {
      sessionStorage.removeItem('gAccessToken');
    }
    setAccessToken(token);
  }, []);
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  const [calMode, setCalMode] = useState('month'); // month | week
  const [weekOffset, setWeekOffset] = useState(0);
  const [activeTab, setActiveTab] = useState('all'); // all | coachColorId
  const [showExport, setShowExport] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showIncomeDetail, setShowIncomeDetail] = useState(false);
  const [showPaymentDetail, setShowPaymentDetail] = useState(false); // ★ 需求3：本月收費明細
  const [showCoachDetail, setShowCoachDetail] = useState(false);
  const [coachDetailSelected, setCoachDetailSelected] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [kvSynced, setKvSynced] = useState(false);
  const [studentOrder, setStudentOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('studentOrder') || '[]'); } catch { return []; }
  });
  const dragState = useRef({ dragId: null, overId: null });

  function saveStudentOrder(order) {
    setStudentOrder(order);
    localStorage.setItem('studentOrder', JSON.stringify(order));
    kvPut('studentOrder', order);
  }

  function getSortedStudents(list) {
    if (!studentOrder || studentOrder.length === 0) return list;
    const orderMap = new Map(studentOrder.map((id, i) => [id, i]));
    return [...list].sort((a, b) => {
      const ai = orderMap.has(a.id) ? orderMap.get(a.id) : 9999;
      const bi = orderMap.has(b.id) ? orderMap.get(b.id) : 9999;
      return ai - bi;
    });
  }

  const [dayDetail, setDayDetail] = useState(null); // { date, events }
  const [showCalendarManager, setShowCalendarManager] = useState(false);
  const [showImportPayments, setShowImportPayments] = useState(false);
  const [showImportProxy, setShowImportProxy] = useState(false); // ★ 需求5：批次匯入代付
  // 學員分類設定（從 localStorage 讀取）
  const [studentGroups, setStudentGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('studentGroups') || '[]'); } catch { return []; }
  });
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const groups = JSON.parse(localStorage.getItem('studentGroups') || '[]');
      const init = {};
      groups.forEach(g => { init[g.id] = true; });
      return init;
    } catch { return {}; }
  });
  const [calendarList, setCalendarList] = useState([]);
  // 從 localStorage 讀取啟用的日曆設定
  const [enabledCalendars, setEnabledCalendars] = useState(() => {
    try { return JSON.parse(localStorage.getItem('enabledCalendars') || 'null') || null; } catch { return null; }
  });
  const [calendarCoachMap, setCalendarCoachMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem('calendarCoachMap') || '{}'); } catch { return {}; }
  });

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAllLessons, setStudentAllLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [studentModalTab, setStudentModalTab] = useState('info');
  const [proxyTargetLessons, setProxyTargetLessons] = useState({}); // { studentId: lessons[] }
  const [coachColorMap, setCoachColorMap] = useState(getCoachColorSettings());

  const { students, addStudent, updateStudent, deleteStudent, addPayment, deletePayment, updatePayment, getPurchased, getTotalAmount, getPerLesson, getBalance, addProxyLesson } = useStudents(kvSynced);
  const { events, loading, error, fetchEvents } = useCalendar(accessToken);

  // KV 載入：登入後從 KV 同步資料
  // sessionStorage 的作用：同一個分頁 reload 後不重複同步（防止無限 reload 迴圈）
  // 其他裝置或新分頁的 sessionStorage 是乾淨的，所以跨裝置同步仍然有效
  useEffect(() => {
    if (!accessToken || kvSynced) return;
    // 這個 session 已經同步過了（reload 後的第二次進入），直接標記完成
    if (sessionStorage.getItem('kvSyncDone') === '1') {
      setKvSynced(true);
      return;
    }
    setKvSynced(true);
    async function syncFromKV() {
      try {
        const [kvStudents, kvGroups, kvSettings, kvOrder] = await Promise.all([
          kvGet('students'), kvGet('groups'), kvGet('settings'), kvGet('studentOrder'),
        ]);
        let hasUpdate = false;
        if (kvStudents && Array.isArray(kvStudents) && kvStudents.length > 0) {
          localStorage.setItem('lessonManagerStudents', JSON.stringify(kvStudents));
          hasUpdate = true;
        }
        if (kvGroups && Array.isArray(kvGroups) && kvGroups.length > 0) {
          localStorage.setItem('studentGroups', JSON.stringify(kvGroups));
          hasUpdate = true;
        }
        if (kvOrder && Array.isArray(kvOrder) && kvOrder.length > 0) {
          localStorage.setItem('studentOrder', JSON.stringify(kvOrder));
          hasUpdate = true;
        }
        if (kvSettings?.calendarCoachMap) {
          localStorage.setItem('calendarCoachMap', JSON.stringify(kvSettings.calendarCoachMap));
          hasUpdate = true;
        }
        if (kvSettings?.enabledCalendars) {
          localStorage.setItem('enabledCalendars', JSON.stringify(kvSettings.enabledCalendars));
          hasUpdate = true;
        }
        // reload 前先標記，reload 後同一分頁不會再次同步（防止無限迴圈）
        sessionStorage.setItem('kvSyncDone', '1');
        if (hasUpdate) window.location.reload();
      } catch(e) {
        console.error('KV sync failed', e);
        // 失敗也標記，避免一直重試造成 reload 迴圈
        sessionStorage.setItem('kvSyncDone', '1');
      }
    }
    syncFromKV();
  }, [accessToken]);

  // KV 自動儲存：groups / studentOrder / settings 變動時同步
  // Bug fix: students 的 KV 同步已由 useStudents hook 負責，這裡不重複處理
  // 避免兩個地方競爭寫入 /students 導致互相覆蓋
  const kvSyncTimer = useRef(null);
  useEffect(() => {
    if (!kvSynced) return;
    clearTimeout(kvSyncTimer.current);
    kvSyncTimer.current = setTimeout(() => {
      kvPut('groups', JSON.parse(localStorage.getItem('studentGroups') || '[]'));
      kvPut('studentOrder', JSON.parse(localStorage.getItem('studentOrder') || '[]'));
      kvPut('settings', {
        calendarCoachMap: JSON.parse(localStorage.getItem('calendarCoachMap') || '{}'),
        enabledCalendars: JSON.parse(localStorage.getItem('enabledCalendars') || '{}'),
      });
    }, 1000);
  }, [studentGroups, studentOrder, calendarCoachMap, enabledCalendars, kvSynced]);

  // 取得所有教練列表（從顏色設定）
  // 從事件偵測到的日曆 ID 清單
  const detectedCalendarIds = [...new Set(events.map(ev => ev.calendarId).filter(Boolean))];

  // 教練清單建立邏輯：
  // 1. 從日曆管理（calendarCoachMap）
  // 2. 從顏色設定（coachColorMap）
  // 3. 從事件偵測到的日曆，即使沒有設定名稱也顯示（用日曆名稱）
  const coachesMap = new Map();

  // 優先：日曆管理設定
  calendarList
    .filter(cal => calendarCoachMap[cal.id])
    .forEach(cal => {
      coachesMap.set(cal.id, {
        colorId: cal.id, calendarId: cal.id,
        name: calendarCoachMap[cal.id],
        hex: cal.backgroundColor || '#888'
      });
    });

  // 次要：顏色設定（可能包含用日曆 ID 存的設定）
  Object.entries(coachColorMap)
    .filter(([id, name]) => name && id !== '__default__')
    .forEach(([id, name]) => {
      if (!coachesMap.has(id)) {
        const cal = calendarList.find(c => c.id === id);
        coachesMap.set(id, {
          colorId: id, calendarId: cal ? id : null,
          name, hex: cal?.backgroundColor || COLOR_MAP[id]?.hex || '#888'
        });
      }
    });

  // 補充：事件中出現的日曆，若還沒有對應名稱，用日曆名稱代替
  detectedCalendarIds.forEach(calId => {
    if (!coachesMap.has(calId)) {
      const cal = calendarList.find(c => c.id === calId);
      if (cal) {
        coachesMap.set(calId, {
          colorId: calId, calendarId: calId,
          name: cal.summary,
          hex: cal.backgroundColor || '#888'
        });
      }
    }
  });

  // 最終以「教練名稱」去重，避免同一位教練從不同來源重複出現
  // 只保留 enabledCalendars 有啟用的日曆對應的教練
  const enabledCalendarIds = enabledCalendars
    ? new Set(Object.entries(enabledCalendars).filter(([,v]) => v).map(([id]) => id))
    : null;

  const namesSeen = new Set();
  const coaches = [...coachesMap.values()].filter(c => {
    if (enabledCalendarIds) {
      // calendarId 為 null 代表行事曆已不存在，排除
      if (!c.calendarId) return false;
      // calendarId 不在啟用清單裡，排除
      if (!enabledCalendarIds.has(c.calendarId)) return false;
    }
    if (namesSeen.has(c.name)) return false;
    namesSeen.add(c.name);
    return true;
  });
  const allCoaches = coaches;

  // 從日曆 ID 或 colorId 取得教練名稱
  function getCoachNameForEvent(ev) {
    if (ev.calendarId && calendarCoachMap[ev.calendarId]) return calendarCoachMap[ev.calendarId];
    if (ev.colorId && coachColorMap[ev.colorId]) return coachColorMap[ev.colorId];
    return null;
  }

  // 從日曆 ID 或 colorId 取得教練顏色
  function getCoachHexForEvent(ev) {
    if (ev.calendarId) {
      const cal = calendarList.find(c => c.id === ev.calendarId);
      if (cal?.backgroundColor) return cal.backgroundColor;
    }
    if (ev.colorId) return COLOR_MAP[ev.colorId]?.hex || '#aaa';
    return '#aaaaaa';
  }

  // 從行事曆事件中偵測使用中的 colorId（單一日曆模式）
  const detectedColorIds = [...new Set(events.map(ev => ev.colorId).filter(Boolean))];
  // 偵測使用中的日曆 ID（多日曆模式）

  // 快取所有月份事件，供匯出與全局統計使用
  const allEventsCache = useRef({});
  const [allEvents, setAllEvents] = useState([]); // 所有已載入月份的事件
  const allEventsInitialized = useRef(false);

  async function fetchEventsForRange(startDate, endDate) {
    // 取得所有啟用日曆
    const activeCalendars = enabledCalendars
      ? Object.entries(enabledCalendars).filter(([,v]) => v).map(([id]) => id)
      : [process.env.REACT_APP_CALENDAR_ID].filter(Boolean);

    const timeMin = startDate.toISOString();
    const timeMax = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0, 23, 59, 59).toISOString();

    const results = [];
    await Promise.all(activeCalendars.map(async calId => {
      try {
        let pageToken = null;
        do {
          const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calId)}/events` +
            `?timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&maxResults=2500` +
            (pageToken ? `&pageToken=${pageToken}` : '');
          const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
          if (!res.ok) break;
          const data = await res.json();
          (data.items || []).forEach(ev => { ev._calendarId = calId; });
          results.push(...(data.items || []));
          pageToken = data.nextPageToken || null;
        } while (pageToken);
      } catch(e) { console.error(e); }
    }));
    // 空的 results placeholder to match original return structure
    return results;
  }

  // 解析 Google Calendar 原始事件為程式格式
  function parseRawEvents(rawItems, knownNames = null) {
    const parsed = [];
    (rawItems || []).forEach(ev => {
      // 優先用日曆 ID 對應教練（新架構），fallback 用 colorId（舊架構）
      const sourceCalId = ev._calendarId || null;
      const colorId = ev.colorId || null;
      const startDT = ev.start?.dateTime || ev.start?.date || '';
      const startDate = startDT ? new Date(startDT) : null;
      if (!startDate) return;
      const dateStr = `${startDate.getFullYear()}/${startDate.getMonth()+1}/${startDate.getDate()}`;
      const timeStr = ev.start?.dateTime
        ? `${String(startDate.getHours()).padStart(2,'0')}:${String(startDate.getMinutes()).padStart(2,'0')}`
        : '';
      // 先用分隔符號切開標題
      const rawNames = (ev.summary || '').split(/[、,，]/).map(s => s.trim()).filter(Boolean);
      rawNames.forEach(name => {
        // 如果有已知學員名單，只接受完全符合的名字
        if (knownNames && knownNames.size > 0 && !knownNames.has(name)) return;
        parsed.push({ studentName: name, colorId, calendarId: sourceCalId, date: dateStr, time: timeStr, isTrial: false });
      });
    });
    return parsed;
  }

  // 抓取使用者所有日曆清單
  async function fetchCalendarList() {
    try {
      const res = await fetch(
        'https://www.googleapis.com/calendar/v3/users/me/calendarList?maxResults=250',
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const data = await res.json();
      const items = (data.items || []).filter(c => c.accessRole !== 'freeBusyReader');
      setCalendarList(items);
      // 如果還沒有設定過，預設全部啟用
      if (!enabledCalendars) {
        const defaultEnabled = {};
        items.forEach(c => { defaultEnabled[c.id] = true; });
        setEnabledCalendars(defaultEnabled);
        localStorage.setItem('enabledCalendars', JSON.stringify(defaultEnabled));
      }
      return items;
    } catch(e) { console.error(e); return []; }
  }

  // 從單一日曆抓取所有歷史事件（支援分頁）
  async function fetchCalendarEvents(calendarId, startDateStr) {
    const timeMin = new Date(startDateStr).toISOString();
    const timeMax = new Date().toISOString();
    let allItems = [];
    let pageToken = null;
    do {
      const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events` +
        `?timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&maxResults=2500` +
        (pageToken ? `&pageToken=${pageToken}` : '');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!res.ok) break;
      const data = await res.json();
      (data.items || []).forEach(ev => { ev._calendarId = calendarId; });
      allItems = allItems.concat(data.items || []);
      pageToken = data.nextPageToken || null;
    } while (pageToken);
    return allItems;
  }

  // 抓取所有啟用日曆的歷史事件
  async function fetchAllHistoryEvents(startDateStr) {
    const activeCalendars = enabledCalendars
      ? Object.entries(enabledCalendars).filter(([,v]) => v).map(([id]) => id)
      : [process.env.REACT_APP_CALENDAR_ID].filter(Boolean);
    const results = await Promise.all(
      activeCalendars.map(id => fetchCalendarEvents(id, startDateStr))
    );
    return results.flat();
  }

  // 登入後自動取得日曆清單（讓顏色立刻顯示）
  useEffect(() => {
    if (!accessToken) return;
    fetchCalendarList();
  }, [accessToken]);

  // 登入後或學員名單改變時，抓取從統計起始日到今天的所有事件
  useEffect(() => {
    if (!accessToken) return;
    if (students.length === 0) return;
    if (allEventsInitialized.current) return;
    allEventsInitialized.current = true;

    fetchAllHistoryEvents(`${new Date().getFullYear() - 3}-01-01`).then(items => {
      const knownNames = new Set(students.map(s => s.name).filter(Boolean));
      const parsed = parseRawEvents(items, knownNames.size > 0 ? knownNames : null);
      setAllEvents(parsed);
    }).catch(() => {});
  }, [accessToken, students.length]);

  // 手動重新整理
  async function refreshCalendar() {
    const key = `${currentYear}-${currentMonth}`;
    delete allEventsCache.current[key];
    fetchEvents(currentYear, currentMonth);
    allEventsInitialized.current = false;
    try {
      const items = await fetchAllHistoryEvents(`${new Date().getFullYear() - 3}-01-01`);
      const knownNames = new Set(students.map(s => s.name).filter(Boolean));
      setAllEvents(parseRawEvents(items, knownNames.size > 0 ? knownNames : null));
    } catch(e) { console.error(e); }
    allEventsInitialized.current = true;
  }

  // 每 5 分鐘自動更新行事曆
  useEffect(() => {
    if (!accessToken) return;
    const timer = setInterval(() => {
      const key = `${currentYear}-${currentMonth}`;
      delete allEventsCache.current[key];
      fetchEvents(currentYear, currentMonth);
    }, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [accessToken, currentYear, currentMonth, fetchEvents]);

  // 每 30 秒從 KV 拉一次最新學員資料（讓已開著的分頁能感知到其他裝置的修改）
  // 只在 kvSynced 完成後才啟動，且只更新有差異的欄位，不觸發 reload
  useEffect(() => {
    if (!accessToken || !kvSynced) return;
    const poll = setInterval(async () => {
      try {
        const kvStudents = await kvGet('students');
        if (!kvStudents || !Array.isArray(kvStudents) || kvStudents.length === 0) return;
        const local = localStorage.getItem('lessonManagerStudents');
        // 用 JSON 字串比對，有差異才更新（避免不必要的 re-render）
        const kvStr = JSON.stringify(kvStudents);
        if (local === kvStr) return;
        // KV 有新資料，寫入 localStorage 並 reload
        localStorage.setItem('lessonManagerStudents', kvStr);
        sessionStorage.removeItem('kvSyncDone'); // 清除，讓 reload 後重新同步其他資料
        window.location.reload();
      } catch(e) { /* 網路失敗靜默忽略 */ }
    }, 30 * 1000);
    return () => clearInterval(poll);
  }, [accessToken, kvSynced]);

  useEffect(() => {
    if (!accessToken) return;
    fetchEvents(currentYear, currentMonth);
  }, [accessToken, currentYear, currentMonth, fetchEvents]);

  // 週視圖：當 weekOffset 改變時，確保該週的月份事件都有載入
  useEffect(() => {
    if (!accessToken || calMode !== 'week') return;
    const now = new Date();
    const base = new Date(now.getTime() + weekOffset * 7 * 24 * 3600 * 1000);
    const weekStart = new Date(base); weekStart.setDate(base.getDate() - base.getDay());
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
    // 如果週跨越兩個月，兩個月都要抓
    const months = [];
    if (weekStart.getFullYear() !== currentYear || weekStart.getMonth() + 1 !== currentMonth) {
      months.push({ year: weekStart.getFullYear(), month: weekStart.getMonth() + 1 });
    }
    if (weekEnd.getMonth() !== weekStart.getMonth()) {
      months.push({ year: weekEnd.getFullYear(), month: weekEnd.getMonth() + 1 });
    }
    months.forEach(({ year, month }) => fetchEvents(year, month));
  }, [accessToken, calMode, weekOffset, fetchEvents]);

  function backupData() {
    const data = {
      students: localStorage.getItem('lessonManagerStudents'),
      groups: localStorage.getItem('studentGroups'),
      enabledCalendars: localStorage.getItem('enabledCalendars'),
      calendarCoachMap: localStorage.getItem('calendarCoachMap'),
      coachColorSettings: localStorage.getItem('coachColorSettings'),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const d = new Date();
    a.download = `瑪斯特備份_${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}.json`;
    a.click();
  }

  function restoreData(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const data = JSON.parse(ev.target.result);
        const keyMap = {
          students: 'lessonManagerStudents',
          groups: 'studentGroups',
          enabledCalendars: 'enabledCalendars',
          calendarCoachMap: 'calendarCoachMap',
          coachColorSettings: 'coachColorSettings',
        };
        Object.entries(keyMap).forEach(([k, lsKey]) => {
          if (data[k]) localStorage.setItem(lsKey, data[k]);
        });
        alert('還原成功！即將重新整理頁面。');
        window.location.reload();
      } catch(err) {
        alert('還原失敗：檔案格式不正確');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function navMonth(dir) {
    let m = currentMonth + dir;
    let y = currentYear;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setCurrentMonth(m);
    setCurrentYear(y);
  }

  // 計算每位學員的已上課堂數（從行事曆事件）
  // 計算已上堂數：只計算今天以前（含今天）的課程，固定不隨行事曆變動
  function getUsedLessons(student) {
    const todayStr = (() => {
      const n = new Date();
      return `${n.getFullYear()}/${n.getMonth()+1}/${n.getDate()}`;
    })();
    const todayDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    const todayEvents = allEvents.filter(ev => {
      if (!ev.date) return false;
      const p = ev.date.split('/');
      const evDate = new Date(parseInt(p[0]), parseInt(p[1])-1, parseInt(p[2]));
      return evDate <= todayDate;
    });
    return countLessonsForStudent(todayEvents, student.name, true);
  }

  function getRemaining(student) {
    return getPurchased(student) - getUsedLessons(student);
  }

  // ★ 需求2 & 需求6：金額餘額（Credit-Based System）
  // 若 baseRate 未設定，fallback 到舊的堂數剩餘 × perLesson
  function getStudentBalance(student) {
    const baseRate = Number(student.baseRate) || 0;
    if (baseRate === 0) return null; // 未設定 baseRate，回傳 null 代表不套用新制
    return getBalance(student, getUsedLessons(student));
  }

  // ★ 需求2：判斷是否欠費（餘額 < baseRate）
  function isStudentOverdue(student) {
    const baseRate = Number(student.baseRate) || 0;
    if (baseRate === 0) {
      // 舊制：剩餘堂數 <= 0
      return getRemaining(student) <= 0 && (student.payments || []).length > 0;
    }
    const balance = getStudentBalance(student);
    return balance !== null && balance < baseRate;
  }

  // 催費置頂排序：欠費學員依欠費金額由多到少，其餘維持 studentOrder（可自由拖拉）
  function getSortedStudentsWithOverdue(list) {
    const overdue = list.filter(s => isStudentOverdue(s));
    const normal = list.filter(s => !isStudentOverdue(s));
    // 欠費金額從多到少（balance 越小越負，排越前面）
    const sortedOverdue = [...overdue].sort((a, b) => {
      const balA = getStudentBalance(a) ?? (getRemaining(a) * getPerLesson(a));
      const balB = getStudentBalance(b) ?? (getRemaining(b) * getPerLesson(b));
      return balA - balB; // 最負的排最前
    });
    const sortedNormal = getSortedStudents(normal);
    return [...sortedOverdue, ...sortedNormal];
  }

  // 抓取某位學員的上課紀錄（回傳陣列，不設 state，供代付勾選使用）
  async function fetchAllLessonsForStudentRaw(studentName) {
    try {
      const endDate = new Date();
      const startDate = new Date(endDate.getFullYear() - 3, 0, 1);
      const activeCalendars = enabledCalendars
        ? Object.entries(enabledCalendars).filter(([,v]) => v).map(([id]) => id)
        : [process.env.REACT_APP_CALENDAR_ID].filter(Boolean);
      const allItems = [];
      await Promise.all(activeCalendars.map(async calendarId => {
        try {
          const res = await fetch(
            `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events` +
            `?timeMin=${startDate.toISOString()}&timeMax=${endDate.toISOString()}&singleEvents=true&orderBy=startTime&maxResults=2500` +
            `&q=${encodeURIComponent(studentName)}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          if (!res.ok) return;
          const data = await res.json();
          (data.items || []).forEach(ev => { ev._calendarId = calendarId; });
          allItems.push(...(data.items || []));
        } catch(e) {}
      }));
      const lessons = [];
      allItems.forEach(ev => {
        const names = (ev.summary || '').split(/[、,，]/).map(s => s.trim()).filter(Boolean);
        if (!names.includes(studentName)) return;
        const startDT = ev.start?.dateTime || ev.start?.date || '';
        const sd = startDT ? new Date(startDT) : null;
        if (!sd) return;
        const dateStr = `${sd.getFullYear()}/${sd.getMonth()+1}/${sd.getDate()}`;
        const timeStr = ev.start?.dateTime ? `${String(sd.getHours()).padStart(2,'0')}:${String(sd.getMinutes()).padStart(2,'0')}` : '';
        lessons.push({ studentName, date: dateStr, time: timeStr });
      });
      return lessons.sort((a, b) => new Date(a.date.replace(/\//g,'-')) - new Date(b.date.replace(/\//g,'-')));
    } catch(e) { return []; }
  }

  // 抓取某位學員的所有歷史上課紀錄（從最早到今天）
  async function fetchAllLessonsForStudent(studentName) {
    setLoadingLessons(true);
    setStudentAllLessons([]);
    try {
      const endDate = new Date();
      const startDate = new Date(endDate.getFullYear() - 3, 0, 1);
      const timeMin = startDate.toISOString();
      const timeMax = endDate.toISOString();

      // 讀取所有啟用的日曆
      const activeCalendars = enabledCalendars
        ? Object.entries(enabledCalendars).filter(([,v]) => v).map(([id]) => id)
        : [process.env.REACT_APP_CALENDAR_ID].filter(Boolean);

      // 並行從所有日曆搜尋該學員
      const allItems = [];
      await Promise.all(activeCalendars.map(async calendarId => {
        try {
          const res = await fetch(
            `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events` +
            `?timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&maxResults=2500` +
            `&q=${encodeURIComponent(studentName)}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          if (!res.ok) return;
          const data = await res.json();
          (data.items || []).forEach(ev => { ev._calendarId = calendarId; });
          allItems.push(...(data.items || []));
        } catch(e) { console.error(e); }
      }));

      const lessons = [];
      allItems.forEach(ev => {
        const names = (ev.summary || '').split(/[、,，]/).map(s => s.trim()).filter(Boolean);
        if (!names.includes(studentName)) return;
        const colorId = ev.colorId || null;
        const calendarId = ev._calendarId || null;
        const startDT = ev.start?.dateTime || ev.start?.date || '';
        const sd = startDT ? new Date(startDT) : null;
        if (!sd) return;
        const dateStr = `${sd.getFullYear()}/${sd.getMonth()+1}/${sd.getDate()}`;
        const timeStr = ev.start?.dateTime
          ? `${String(sd.getHours()).padStart(2,'0')}:${String(sd.getMinutes()).padStart(2,'0')}`
          : '';
        lessons.push({ studentName, colorId, calendarId, date: dateStr, time: timeStr, isTrial: false, notes: ev.description || '' });
      });

      lessons.sort((a, b) => new Date(b.date.replace(/\//g,'-')) - new Date(a.date.replace(/\//g,'-')));
      setStudentAllLessons(lessons);
    } catch(e) {
      console.error(e);
    } finally {
      setLoadingLessons(false);
    }
  }

  // 篩選要顯示的事件（依分頁）
  const filteredEvents = activeTab === 'all'
    ? events
    : events.filter(ev => {
        const tabCoach = allCoaches.find(c => c.colorId === activeTab);
        if (!tabCoach) return false;
        // 比對 calendarId 或 colorId
        return ev.calendarId === activeTab || ev.colorId === activeTab ||
               (tabCoach.calendarId && ev.calendarId === tabCoach.calendarId);
      });

  // 篩選要顯示的學員（依分頁）
  const filteredStudents = activeTab === 'all'
    ? students
    : students.filter(s =>
        events.some(ev => ev.studentName === s.name && (ev.colorId === activeTab || ev.calendarId === activeTab))
      );

  const searchedStudents = searchQuery
    ? filteredStudents.filter(s => s.name.includes(searchQuery))
    : filteredStudents;
  const activeStudents = searchedStudents.filter(s => !s.graduated);
  const graduatedStudents = searchedStudents.filter(s => s.graduated);

  if (!accessToken) {
    return <LoginPage onLogin={handleSetAccessToken} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {/* 頂部導覽 */}
      <div style={styles.topbar}>
        <span style={styles.topbarTitle}>課程管理系統</span>
        <div style={{ display: 'flex', gap: 0 }}>
          <TabBtn active={activeTab === 'all'} onClick={() => setActiveTab('all')}>總覽</TabBtn>
          {allCoaches.map(c => (
            <TabBtn key={c.colorId} active={activeTab === c.colorId} onClick={() => setActiveTab(c.colorId)}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c.hex, marginRight: 5 }} />
              {c.name}
            </TabBtn>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button style={{ ...styles.iconBtn, color: '#1967d2', borderColor: '#b3d4f5' }} onClick={() => { fetchCalendarList(); setShowCalendarManager(true); }} title="日曆管理">🗓 日曆管理</button>
          <button style={{ ...styles.iconBtn, color: '#1967d2', borderColor: '#b3d4f5' }} onClick={backupData} title="備份資料">備份</button>
          <label style={{ ...styles.iconBtn, color: '#b46309', borderColor: '#fde68a', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }} title="還原資料">
            還原
            <input type="file" accept=".json" style={{ display: 'none' }} onChange={restoreData} />
          </label>
          <button style={styles.iconBtn} onClick={() => handleSetAccessToken(null)}>登出</button>
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        {/* 左側學員列表 */}
        <div style={{
          ...styles.leftPanel,
          width: sidebarOpen ? 250 : 0,
          minWidth: sidebarOpen ? 250 : 0,
          padding: sidebarOpen ? 14 : 0,
          overflow: sidebarOpen ? 'auto' : 'hidden',
          transition: 'all 0.25s ease',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={styles.panelHeader}>學員列表</div>
            <button
              style={{ fontSize: 11, color: '#888', background: 'none', border: '0.5px solid #e0e0e0', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}
              onClick={() => setShowGroupManager(true)}
              title="管理分類"
            >分類</button>
          </div>
          <input
            style={{ width: '100%', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', fontSize: 12, marginBottom: 8, background: 'var(--search-bg, #f8f8f8)' }}
            placeholder="搜尋學員..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {(() => {
            const groupedIds = new Set(studentGroups.flatMap(g => g.studentIds || []));
            const sortedActive = getSortedStudentsWithOverdue(activeStudents);
            const ungrouped = sortedActive.filter(s => !groupedIds.has(s.id));
            const makeCard = s => (
              <DraggableStudentCard
                key={s.id}
                student={s}
                remaining={getRemaining(s)}
                balance={getStudentBalance(s)}
                used={getUsedLessons(s)}
                purchased={getPurchased(s)}
                overdue={isStudentOverdue(s)}
                active={selectedStudent?.id === s.id}
                onClick={() => { setSelectedStudent(s); setStudentModalTab('info'); fetchAllLessonsForStudent(s.name); }}
                dragState={dragState}
                allStudents={sortedActive}
                onReorder={saveStudentOrder}
              />
            );
            return (
              <>
                {studentGroups.map((group, groupIdx) => {
                  const groupStudents = getSortedStudentsWithOverdue(activeStudents.filter(s => (group.studentIds || []).includes(s.id)));
                  if (groupStudents.length === 0) return null;
                  const isCollapsed = collapsedGroups[group.id];
                  return (
                    <div
                      key={group.id}
                      style={{ marginBottom: 8 }}
                      draggable
                      onDragStart={e => { e.dataTransfer.setData('groupId', group.id); }}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => {
                        e.preventDefault();
                        const fromId = e.dataTransfer.getData('groupId');
                        if (!fromId || fromId === group.id) return;
                        setStudentGroups(prev => {
                          const arr = [...prev];
                          const fromIdx = arr.findIndex(g => g.id === fromId);
                          const toIdx = arr.findIndex(g => g.id === group.id);
                          if (fromIdx < 0 || toIdx < 0) return prev;
                          const [moved] = arr.splice(fromIdx, 1);
                          arr.splice(toIdx, 0, moved);
                          return arr;
                        });
                      }}
                    >
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px', cursor: 'pointer', borderRadius: 6 }}
                        onClick={() => setCollapsedGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                      >
                        <span style={{ fontSize: 10, color: '#ccc', cursor: 'grab', padding: '0 2px' }} title="拖拉排序">⠿</span>
                        <span style={{ fontSize: 10, color: '#aaa', width: 10 }}>{isCollapsed ? '▶' : '▼'}</span>
                        <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                        <span style={{ fontSize: 11, fontWeight: 500, color: '#888', whiteSpace: 'nowrap', padding: '0 4px', background: group.color || '#f5f5f5', borderRadius: 4 }}>
                          {group.name} ({groupStudents.length})
                        </span>
                        <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                      </div>
                      {!isCollapsed && groupStudents.map(makeCard)}
                    </div>
                  );
                })}
                {ungrouped.length > 0 && ungrouped.map(makeCard)}
              </>
            );
          })()}
          <button
            style={{ width: '100%', padding: '8px', border: '0.5px dashed #ccc', borderRadius: 8, fontSize: 12, color: '#888', background: 'none', marginTop: 8 }}
            onClick={() => {
              const s = addStudent({ name: '新學員' });
              setSelectedStudent(s);
              setStudentModalTab('info');
            }}
          >
            + 新增學員
          </button>
          <button
            style={{ width: '100%', padding: '8px', border: '0.5px dashed #b3d9ff', borderRadius: 8, fontSize: 12, color: '#1967d2', background: 'none', marginTop: 6 }}
            onClick={() => setShowImportConfirm(true)}
          >
            從行事曆自動匯入學員
          </button>
          <button
            style={{ width: '100%', padding: '8px', border: '0.5px dashed #c8e6c9', borderRadius: 8, fontSize: 12, color: '#188038', background: 'none', marginTop: 6 }}
            onClick={() => setShowImportPayments(true)}
          >
            批次匯入付款紀錄
          </button>
          <button
            style={{ width: '100%', padding: '8px', border: '0.5px dashed #e1bee7', borderRadius: 8, fontSize: 12, color: '#7b1fa2', background: 'none', marginTop: 6 }}
            onClick={() => setShowImportProxy(true)}
          >
            批次匯入代付紀錄
          </button>
          <button
            style={{ width: '100%', padding: '8px', border: '0.5px dashed #ffcdd2', borderRadius: 8, fontSize: 12, color: '#c5221f', background: 'none', marginTop: 6 }}
            onClick={() => {
              if (window.confirm('確定要清除所有學員資料嗎？此動作無法復原。')) {
                localStorage.removeItem('lessonManagerStudents');
                localStorage.removeItem('studentGroups');
                window.location.reload();
              }
            }}
          >
            清除所有學員資料
          </button>

          {graduatedStudents.length > 0 && (
            <>
              <div style={{ ...styles.panelHeader, marginTop: 16 }}>已畢業</div>
              {graduatedStudents.map(s => (
                <StudentCard
                  key={s.id}
                  student={s}
                  remaining={getRemaining(s)}
                  balance={getStudentBalance(s)}
                  used={getUsedLessons(s)}
                  purchased={getPurchased(s)}
                  active={selectedStudent?.id === s.id}
                  onClick={() => { setSelectedStudent(s); setStudentModalTab('info'); fetchAllLessonsForStudent(s.name); }}
                  graduated
                />
              ))}
            </>
          )}
        </div>

        {/* 側欄邊緣收合/展開按鈕 */}
        <div
          onClick={() => setSidebarOpen(v => !v)}
          title={sidebarOpen ? '收合側欄' : '展開側欄'}
          style={{
            width: 20, flexShrink: 0,
            background: '#f0f0f0',
            borderRight: '0.5px solid #e0e0e0',
            borderLeft: sidebarOpen ? 'none' : '0.5px solid #e0e0e0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'background 0.15s',
            userSelect: 'none', zIndex: 1,
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#e0e0e0'}
          onMouseLeave={e => e.currentTarget.style.background = '#f0f0f0'}
        >
          <div style={{
            width: 7, height: 7,
            borderLeft: '2px solid #666',
            borderBottom: '2px solid #666',
            transform: sidebarOpen ? 'rotate(45deg)' : 'rotate(-135deg)',
            transition: 'transform 0.25s',
            marginLeft: sidebarOpen ? 3 : -3,
          }} />
        </div>

        {/* 右側主區域 */}
        <div style={styles.rightPanel}>
          {/* 統計卡片 */}
          <StatsRow events={events} students={students} getUsedLessons={getUsedLessons} getPerLesson={getPerLesson} currentMonth={currentMonth} currentYear={currentYear} calMode={calMode} weekOffset={weekOffset} coaches={allCoaches} calendarCoachMap={calendarCoachMap} onShowIncomeDetail={() => setShowIncomeDetail(true)} onShowCoachDetail={(coach) => { setCoachDetailSelected(coach); setShowCoachDetail(true); }} onShowPaymentDetail={() => setShowPaymentDetail(true)} />

          {/* 行事曆標題列 */}
          <div style={styles.calHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                {calMode === 'month'
                  ? `${currentYear}年 ${currentMonth}月`
                  : `第 ${getWeekLabel(weekOffset)} 週`}
              </span>
              <button style={styles.navBtn} onClick={() => calMode === 'month' ? navMonth(-1) : setWeekOffset(w => w - 1)}>←</button>
              <button style={styles.navBtn} onClick={() => { if (calMode === 'month') { setCurrentYear(today.getFullYear()); setCurrentMonth(today.getMonth() + 1); } else setWeekOffset(0); }}>今天</button>
              <button style={styles.navBtn} onClick={() => calMode === 'month' ? navMonth(1) : setWeekOffset(w => w + 1)}>→</button>
              <button style={{ ...styles.navBtn, color: loading ? '#aaa' : '#188038', borderColor: '#c8e6c9' }} onClick={refreshCalendar} disabled={loading} title="重新整理行事曆">
                {loading ? '更新中...' : '↻ 更新'}
              </button>
            </div>
            <div style={styles.modeToggle}>
              <button style={{ ...styles.modeBtn, ...(calMode === 'month' ? styles.modeBtnActive : {}) }} onClick={() => setCalMode('month')}>月</button>
              <button style={{ ...styles.modeBtn, ...(calMode === 'week' ? styles.modeBtnActive : {}) }} onClick={() => {
                if (calMode !== 'week') {
                  // 切換到週視圖時，把 weekOffset 設到對應 currentYear/currentMonth 的第一週
                  const targetDate = new Date(currentYear, currentMonth - 1, 1);
                  const now = new Date();
                  const diffMs = targetDate - now;
                  const diffWeeks = Math.round(diffMs / (7 * 24 * 3600 * 1000));
                  setWeekOffset(diffWeeks);
                }
                setCalMode('week');
              }}>週</button>
            </div>
          </div>

          {/* 圖例 */}
          <div style={styles.legend}>
            {coaches.map(c => (
              <div key={c.colorId} style={styles.legendItem}>
                <div style={{ width: 9, height: 9, borderRadius: '50%', background: c.hex }} />
                <span>{c.name}</span>
              </div>
            ))}
            {coaches.length === 0 && (
              <span style={{ fontSize: 12, color: '#f57c00' }}>
                ⚠ 請先點右上角「設定」，設定顏色對應教練
              </span>
            )}
          </div>

          {/* 載入狀態 */}
          {loading && <div style={{ textAlign: 'center', padding: 20, fontSize: 13, color: '#888' }}>載入行事曆中...</div>}
          {error && <div style={{ padding: '10px 12px', background: '#fce8e6', borderRadius: 8, fontSize: 13, color: '#c5221f', marginBottom: 12 }}>讀取失敗：{error}</div>}

          {/* 行事曆 */}
          {!loading && (
            calMode === 'month'
              ? <MonthCalendar year={currentYear} month={currentMonth} events={filteredEvents} coachColorMap={coachColorMap} calendarList={calendarList} onDayClick={(date, evs) => setDayDetail({ date, events: evs })} />
              : <WeekCalendar weekOffset={weekOffset} events={filteredEvents} coachColorMap={coachColorMap} calendarList={calendarList} />
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button style={styles.exportBtn} onClick={() => setShowExport(true)}>匯出 Excel / Google Sheets</button>
          </div>
        </div>
      </div>

      {/* 學員詳細視窗 */}
      {selectedStudent && (() => {
        // 每次 students 更新，從最新資料取得該學員（確保付款新增後即時反映）
        const latestStudent = students.find(s => s.id === selectedStudent.id) || selectedStudent;
        return (
          <StudentModal
            student={latestStudent}
            tab={studentModalTab}
            setTab={setStudentModalTab}
            used={getUsedLessons(latestStudent)}
            purchased={getPurchased(latestStudent)}
            perLesson={getPerLesson(latestStudent)}
            balance={getStudentBalance(latestStudent)}
            lessons={studentAllLessons}
            loadingLessons={loadingLessons}
            coachColorMap={coachColorMap}
            calendarCoachMap={calendarCoachMap}
            calendarList={calendarList}
            allStudents={students}
            proxyTargetLessons={proxyTargetLessons}
            onFetchProxyLessons={async (studentId, studentName) => {
              if (proxyTargetLessons[studentId]) return; // 已抓過
              const items = await fetchAllLessonsForStudentRaw(studentName);
              setProxyTargetLessons(prev => ({ ...prev, [studentId]: items }));
            }}
            onUpdate={updates => {
              updateStudent(latestStudent.id, updates);
              setSelectedStudent(prev => ({ ...prev, ...updates }));
            }}
            onAddPayment={payment => addPayment(latestStudent.id, payment)}
            onDeletePayment={paymentId => deletePayment(latestStudent.id, paymentId)}
            onUpdatePayment={(paymentId, updates) => updatePayment(latestStudent.id, paymentId, updates)}
            onDeleteStudent={() => {
              const id = latestStudent.id;
              deleteStudent(id);
              setSelectedStudent(null);
              // 從所有分類移除已刪除的學員 ID
              setStudentGroups(prev => prev.map(g => ({
                ...g,
                studentIds: (g.studentIds || []).filter(sid => sid !== id),
              })));
            }}
            onAddProxyLesson={(beneficiaryId, beneficiaryBaseRate, lessonInfo) => {
              return addProxyLesson(latestStudent.id, beneficiaryId, beneficiaryBaseRate, lessonInfo);
            }}
            onClose={() => setSelectedStudent(null)}
          />
        );
      })()}

      {/* 匯出視窗 */}
      {showExport && (
        <ExportModal
          accessToken={accessToken}
          students={students}
          events={events}
          coachColorMap={coachColorMap}
          calendarCoachMap={calendarCoachMap}
          coaches={allCoaches}
          fetchEventsForRange={fetchEventsForRange}
          studentGroups={studentGroups}
          onClose={() => setShowExport(false)}
        />
      )}

      {showCalendarManager && (
        <CalendarManagerModal
          calendars={calendarList}
          enabledCalendars={enabledCalendars || {}}
          calendarCoachMap={calendarCoachMap}
          onSave={(enabled, coachMap) => {
            // ★ 只保留目前 calendarList 裡存在的 ID，清除已刪除行事曆的殘留資料
            const validIds = new Set(calendarList.map(c => c.id));
            const cleanCoachMap = Object.fromEntries(
              Object.entries(coachMap).filter(([id]) => validIds.has(id))
            );
            const cleanEnabled = Object.fromEntries(
              Object.entries(enabled).filter(([id]) => validIds.has(id))
            );
            setEnabledCalendars(cleanEnabled);
            setCalendarCoachMap(cleanCoachMap);
            localStorage.setItem('enabledCalendars', JSON.stringify(cleanEnabled));
            localStorage.setItem('calendarCoachMap', JSON.stringify(cleanCoachMap));
            setShowCalendarManager(false);
            allEventsInitialized.current = false;
            setTimeout(() => {
              window.location.reload();
            }, 300);
          }}
          onClose={() => setShowCalendarManager(false)}
        />
      )}

      {showCoachDetail && (
        <CoachDetailModal
          events={events}
          students={students}
          coaches={allCoaches}
          selectedCoach={coachDetailSelected}
          getPerLesson={getPerLesson}
          currentMonth={currentMonth}
          currentYear={currentYear}
          calMode={calMode}
          weekOffset={weekOffset}
          calendarCoachMap={calendarCoachMap}
          onClose={() => setShowCoachDetail(false)}
        />
      )}

      {dayDetail && (
        <DayDetailModal
          date={dayDetail.date}
          events={dayDetail.events}
          coachColorMap={coachColorMap}
          calendarCoachMap={calendarCoachMap}
          calendarList={calendarList}
          onClose={() => setDayDetail(null)}
        />
      )}

      {showPaymentDetail && (
        <PaymentDetailModal
          students={students}
          currentYear={currentYear}
          currentMonth={currentMonth}
          onClose={() => setShowPaymentDetail(false)}
        />
      )}

      {showIncomeDetail && (
        <IncomeDetailModal
          events={events}
          students={students}
          getPerLesson={getPerLesson}
          currentMonth={currentMonth}
          currentYear={currentYear}
          onClose={() => setShowIncomeDetail(false)}
        />
      )}

      {/* 從行事曆匯入學員確認視窗 */}
      {showImportConfirm && (
        <ImportConfirmModal
          accessToken={accessToken}
          enabledCalendars={enabledCalendars}
          existingStudents={students}
          onConfirm={(names) => {
            names.forEach((name, i) => addStudent({ name, id: `${Date.now()}_${i}_${Math.random().toString(36).slice(2)}` }));
            allEventsInitialized.current = false;
            setShowImportConfirm(false);
          }}
          onClose={() => setShowImportConfirm(false)}
        />
      )}

      {showGroupManager && (
        <GroupManagerModal
          groups={studentGroups}
          students={students.filter(s => !s.graduated)}
          onSave={groups => {
            setStudentGroups(groups);
            localStorage.setItem('studentGroups', JSON.stringify(groups));
            setShowGroupManager(false);
          }}
          onClose={() => setShowGroupManager(false)}
        />
      )}

      {showImportPayments && (
        <ImportPaymentsModal
          students={students}
          onImport={items => {
            items.forEach(({ studentId, payment }) => {
              addPayment(studentId, payment);
            });
            setShowImportPayments(false);
          }}
          onClose={() => setShowImportPayments(false)}
        />
      )}

      {/* ★ 需求5：批次匯入代付紀錄 */}
      {showImportProxy && (
        <ImportProxyModal
          students={students}
          onImport={items => {
            // items: [{ payerId, beneficiaryId, beneficiaryBaseRate, date, notes }]
            items.forEach(item => {
              addProxyLesson(item.payerId, item.beneficiaryId, item.beneficiaryBaseRate, { date: item.date, notes: item.notes });
            });
            setShowImportProxy(false);
          }}
          getBalance={getStudentBalance}
          onClose={() => setShowImportProxy(false)}
        />
      )}
    </div>
  );
}

// ★ 需求1：編輯付款紀錄的小視窗
function EditPaymentModal({ payment, onSave, onClose }) {
  const isProxy = payment.isProxy;
  const [date, setDate] = useState(payment.date || '');
  const [lessons, setLessons] = useState(payment.lessons ?? '');
  const [amount, setAmount] = useState(payment.amount ?? '');
  const [notes, setNotes] = useState(payment.notes || '');
  const [proxyAmount, setProxyAmount] = useState(payment.proxyAmount ?? '');

  function handleSave() {
    if (isProxy) {
      onSave({
        date,
        proxyAmount: proxyAmount === '' ? 0 : Number(proxyAmount),
        notes,
      });
    } else {
      onSave({
        date,
        lessons: lessons === '' ? 0 : Number(lessons),
        amount: amount === '' ? 0 : Number(amount),
        notes,
      });
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600 }}>
      <div style={{ background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 360, maxWidth: '95vw', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>
            {isProxy ? (payment.proxyType === 'payer' ? '編輯代付紀錄' : '編輯被代付紀錄') : '編輯付款紀錄'}
          </span>
          <button style={{ background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer' }} onClick={onClose}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>日期</label>
            <input type="date" style={EP.input} value={date} onChange={e => setDate(e.target.value)} />
          </div>

          {isProxy ? (
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
                {payment.proxyType === 'payer' ? '代付金額' : '被代付金額'}
              </label>
              <input type="number" style={EP.input} value={proxyAmount} onChange={e => setProxyAmount(e.target.value)} placeholder="0" />
            </div>
          ) : (
            <>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>購買堂數</label>
                <input type="number" style={EP.input} value={lessons} onChange={e => setLessons(e.target.value)} placeholder="0" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>金額</label>
                <input type="number" style={EP.input} value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" />
              </div>
            </>
          )}

          <div>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>備註</label>
            <input style={EP.input} value={notes} onChange={e => setNotes(e.target.value)} placeholder="（選填）" />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button style={EP.btnS} onClick={onClose}>取消</button>
          <button style={EP.btnP} onClick={handleSave}>儲存</button>
        </div>
      </div>
    </div>
  );
}

const EP = {
  input: { width: '100%', border: '0.5px solid #ccc', borderRadius: 8, padding: '7px 10px', fontSize: 13, boxSizing: 'border-box' },
  btnS: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#666', cursor: 'pointer' },
  btnP: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: '#222', border: 'none', color: '#fff', fontWeight: 500, cursor: 'pointer' },
};

// ★ 格式化剩餘餘額顯示：「N 堂餘 $X」或「欠 $X（-N 堂）」
function formatBalance(balance, baseRate) {
  if (baseRate <= 0) return null;
  const fullLessons = Math.floor(balance / baseRate);
  const remainder = balance % baseRate;
  // 負數情況
  if (balance < 0) {
    // 例：-3200 / 1200 → Math.ceil(-3200/1200) = -2 堂，餘 Math.abs(-3200 % 1200)
    const debtLessons = Math.ceil(balance / baseRate); // 負數，例 -2
    const debtRemainder = Math.abs(balance % baseRate); // 例 400
    if (debtRemainder === 0) {
      return { lessons: debtLessons, remainder: 0, text: `欠 ${Math.abs(debtLessons)} 堂`, isDebt: true };
    }
    return { lessons: debtLessons, remainder: debtRemainder, text: `欠 ${Math.abs(debtLessons)} 堂餘欠 $${debtRemainder.toLocaleString()}`, isDebt: true };
  }
  // 正數
  if (remainder === 0) {
    return { lessons: fullLessons, remainder: 0, text: `${fullLessons} 堂`, isDebt: false };
  }
  return { lessons: fullLessons, remainder, text: `${fullLessons} 堂餘 $${remainder.toLocaleString()}`, isDebt: false };
}

function getWeekLabel(offset) {
  const now = new Date();
  const d = new Date(now.getTime() + offset * 7 * 24 * 3600 * 1000);
  const day = d.getDay();
  const mon = new Date(d); mon.setDate(d.getDate() - day);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return `${mon.getMonth() + 1}/${mon.getDate()} – ${sun.getMonth() + 1}/${sun.getDate()}`;
}

// 統計卡片列
function StatsRow({ events, students, getUsedLessons, getPerLesson, currentMonth, currentYear, calMode, weekOffset, coaches, calendarCoachMap, onShowIncomeDetail, onShowCoachDetail, onShowPaymentDetail }) {
  const now = new Date();
  const todayY = now.getFullYear(), todayM = now.getMonth() + 1, todayD = now.getDate();

  // 週視圖：計算本週的起訖日期
  const weekBase = new Date(now.getTime() + (weekOffset || 0) * 7 * 24 * 3600 * 1000);
  const weekStart = new Date(weekBase); weekStart.setDate(weekBase.getDate() - weekBase.getDay());
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);

  function isInWeek(dateStr) {
    if (!dateStr) return false;
    const p = dateStr.split('/');
    const d = new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]));
    return d >= weekStart && d <= weekEnd;
  }

  function isInMonth(dateStr) {
    if (!dateStr) return false;
    const p = dateStr.split('/');
    return parseInt(p[0]) === currentYear && parseInt(p[1]) === currentMonth;
  }

  const isWeekMode = calMode === 'week';

  const periodEvents = events.filter(ev => {
    if (!ev.date || ev.isTrial) return false;
    return isWeekMode ? isInWeek(ev.date) : isInMonth(ev.date);
  });
  const periodTotal = periodEvents.length;
  const activeCount = students.filter(s => !s.graduated).length;

  const periodIncome = students.reduce((sum, s) => {
    const used = events.filter(ev => {
      if (!ev.date || ev.isTrial || ev.studentName !== s.name) return false;
      return isWeekMode ? isInWeek(ev.date) : isInMonth(ev.date);
    }).length;
    // ★ 優先用 baseRate，fallback 舊制均價
    const baseRate = Number(s.baseRate) || 0;
    const perL = baseRate > 0 ? baseRate : getPerLesson(s);
    return sum + used * perL;
  }, 0);

  const monthCollected = students.reduce((sum, s) => {
    return sum + (s.payments || []).filter(p => {
      if (!p.date || p.isProxy) return false; // 排除所有代付紀錄，代付非實收
      const d = new Date(p.date);
      return d.getFullYear() === currentYear && d.getMonth() + 1 === currentMonth;
    }).reduce((a, p) => a + (Number(p.amount) || 0), 0);
  }, 0);

  const isThisMonth = currentYear === todayY && currentMonth === todayM;
  const periodLabel = isWeekMode
    ? `${weekStart.getMonth()+1}/${weekStart.getDate()} – ${weekEnd.getMonth()+1}/${weekEnd.getDate()}`
    : `${currentYear}/${currentMonth}`;

  return (
    <div style={{
      display: 'flex', gap: 10, marginBottom: 14,
      overflowX: 'auto', scrollbarWidth: 'none',
      WebkitOverflowScrolling: 'touch',
    }}>
      <div style={{ ...styles.statCard, minWidth: 120, flexShrink: 0 }}>
        <div style={styles.statLabel}>{isWeekMode ? '本週上課堂數' : '本月上課堂數'}</div>
        <div style={styles.statValue}>{periodTotal}</div>
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{periodLabel}</div>
      </div>
      <div style={{ ...styles.statCard, minWidth: 160, flexShrink: 0, cursor: 'pointer', border: '0.5px solid transparent', transition: 'border-color 0.15s' }}
        onClick={() => onShowCoachDetail && onShowCoachDetail(null)}
        onMouseEnter={e => e.currentTarget.style.borderColor='#ccc'}
        onMouseLeave={e => e.currentTarget.style.borderColor='transparent'}>
        <div style={styles.statLabel}>教練上課狀況</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          {(coaches || []).map(c => {
            const cnt = periodEvents.filter(ev =>
              (c.calendarId && ev.calendarId === c.calendarId) || ev.colorId === c.colorId
            ).length;
            return (
              <span key={c.colorId} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 20, background: c.hex + '28', color: c.hex, border: `0.5px solid ${c.hex}55`, fontWeight: 500 }}>
                {c.name} {cnt}
              </span>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>點擊查看詳細</div>
      </div>
      <div style={{ ...styles.statCard, minWidth: 130, flexShrink: 0, cursor: 'pointer', border: '0.5px solid transparent', transition: 'border-color 0.15s' }}
        onClick={onShowIncomeDetail}
        onMouseEnter={e => e.currentTarget.style.borderColor='#ccc'}
        onMouseLeave={e => e.currentTarget.style.borderColor='transparent'}>
        <div style={styles.statLabel}>{isWeekMode ? '本週課程收入' : '本月課程收入'} {isThisMonth && !isWeekMode ? '↗' : ''}</div>
        <div style={styles.statValue}>${periodIncome.toLocaleString()}</div>
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>點擊查看詳細</div>
      </div>
      <div style={{ ...styles.statCard, minWidth: 120, flexShrink: 0, cursor: 'pointer', border: '0.5px solid transparent', transition: 'border-color 0.15s' }}
        onClick={onShowPaymentDetail}
        onMouseEnter={e => e.currentTarget.style.borderColor='#ccc'}
        onMouseLeave={e => e.currentTarget.style.borderColor='transparent'}>
        <div style={styles.statLabel}>本月已收學費</div>
        <div style={{ ...styles.statValue, color: monthCollected > 0 ? '#188038' : '#222' }}>${monthCollected.toLocaleString()}</div>
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>點擊查看明細</div>
      </div>
    </div>
  );
}


// 取得事件的顯示顏色（支援日曆 ID 和 colorId 兩種模式）
function getEventHex(ev, calList) {
  // 多日曆模式：用日曆的 backgroundColor
  if (ev.calendarId && Array.isArray(calList) && calList.length > 0) {
    const cal = calList.find(c => c.id === ev.calendarId);
    if (cal?.backgroundColor) return cal.backgroundColor;
  }
  // 單日曆模式：用事件的 colorId
  if (ev.colorId && COLOR_MAP[ev.colorId]) return COLOR_MAP[ev.colorId].hex;
  return '#aaaaaa';
}

// 月曆元件
function MonthCalendar({ year, month, events, coachColorMap, calendarList, onDayClick }) {
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayDate = new Date();
  const isCurrentMonth = year === todayDate.getFullYear() && month === todayDate.getMonth() + 1;

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={styles.calWrap}>
      <div style={styles.dowRow}>
        {['日','一','二','三','四','五','六'].map(d => <div key={d} style={styles.dow}>{d}</div>)}
      </div>
      <div style={styles.calCells}>
        {cells.map((d, i) => {
          if (!d) return <div key={`empty-${i}`} style={{ ...styles.cell, opacity: 0.3 }} />;
          const isToday = isCurrentMonth && d === todayDate.getDate();
          const dateStr = `${year}/${month}/${d}`;
          const dayEvs = events.filter(ev => ev.date === dateStr);
          return (
            <div key={d} style={{ ...styles.cell, cursor: dayEvs.length > 0 ? 'pointer' : 'default' }}
              onClick={() => dayEvs.length > 0 && onDayClick && onDayClick(dateStr, dayEvs)}>
              <div style={{ ...styles.cellDate, ...(isToday ? styles.cellDateToday : {}) }}>{d}</div>
              {dayEvs.slice(0, 2).map((ev, j) => {
                const hex = getEventHex(ev, calendarList);
                const bg = hex + '28';
                return (
                  <div key={j} style={{ ...styles.ev, background: bg, color: hex, border: `0.5px solid ${hex}55` }}>
                    {ev.isTrial ? '★' : ''}{ev.studentName}
                  </div>
                );
              })}
              {dayEvs.length > 2 && <div style={{ fontSize: 9, color: '#999', paddingLeft: 3 }}>+{dayEvs.length - 2}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 週曆元件
function WeekCalendar({ weekOffset, events, coachColorMap, calendarList }) {
  const now = new Date();
  const base = new Date(now.getTime() + weekOffset * 7 * 24 * 3600 * 1000);
  const day = base.getDay();
  const monday = new Date(base); monday.setDate(base.getDate() - day);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i);
    return d;
  });
  const dows = ['日','一','二','三','四','五','六'];
  const todayDate = new Date();

  return (
    <div style={styles.calWrap}>
      <div style={styles.weekHeader}>
        {days.map((d, i) => {
          const isToday = d.toDateString() === todayDate.toDateString();
          return (
            <div key={i} style={styles.weekDay}>
              <div style={styles.dow}>{dows[i]}</div>
              <div style={{ ...styles.weekDayNum, ...(isToday ? styles.cellDateToday : {}) }}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      <div style={styles.weekBody}>
        {days.map((d, i) => {
          // ev.date 格式是 YYYY/M/D，確保比對一致
          const dateStr = `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
          const dayEvs = events.filter(ev => {
            if (!ev.date) return false;
            const parts = ev.date.split('/');
            const evStr = `${parts[0]}/${parseInt(parts[1])}/${parseInt(parts[2])}`;
            return evStr === dateStr;
          });
          return (
            <div key={i} style={styles.weekCell}>
              {dayEvs.map((ev, j) => {
                const hex = getEventHex(ev, calendarList);
                const bg = hex + '28';
                return (
                  <div key={j} style={{ ...styles.ev, background: bg, color: hex, border: `0.5px solid ${hex}55`, marginBottom: 3 }}>
                    {ev.isTrial ? '★' : ''}{ev.studentName}
                    {ev.time && <div style={{ fontSize: 9, opacity: 0.8 }}>{ev.time}</div>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 可拖拉學員卡片（支援滑鼠 + 觸控）
function DraggableStudentCard({ student, remaining, balance, used, purchased, overdue, active, onClick, dragState, allStudents, onReorder }) {
  const touchState = useRef({ startY: 0, clone: null, cloneTop: 0, longPressTimer: null, isDragging: false });
  const cardRef = useRef(null);
  const allStudentsRef = useRef(allStudents);
  const onReorderRef = useRef(onReorder);
  useEffect(() => { allStudentsRef.current = allStudents; }, [allStudents]);
  useEffect(() => { onReorderRef.current = onReorder; }, [onReorder]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    function startDrag(e) {
      // 長按時間到，啟動拖拉
      touchState.current.isDragging = true;
      dragState.current.dragId = student.id;
      const touch = e.touches[0];
      touchState.current.startY = touch.clientY;
      const rect = el.getBoundingClientRect();
      const clone = el.cloneNode(true);
      clone.style.cssText = `position:fixed;top:${rect.top}px;left:${rect.left}px;width:${rect.width}px;opacity:0.75;z-index:9999;pointer-events:none;background:#fff;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.18);`;
      document.body.appendChild(clone);
      touchState.current.clone = clone;
      touchState.current.cloneTop = rect.top;
      // 震動回饋（如果裝置支援）
      if (navigator.vibrate) navigator.vibrate(30);
    }

    function handleTouchStart(e) {
      document.getSelection()?.removeAllRanges();
      touchState.current.isDragging = false;
      touchState.current.startY = e.touches[0].clientY;
      // 長按計時器：500ms 後啟動拖拉
      touchState.current.longPressTimer = setTimeout(() => startDrag(e), 500);
    }

    function handleTouchMove(e) {
      const dy = Math.abs(e.touches[0].clientY - touchState.current.startY);
      if (!touchState.current.isDragging) {
        // 還沒進入拖拉模式：若移動超過 8px 就取消長按計時
        if (dy > 8) {
          clearTimeout(touchState.current.longPressTimer);
          touchState.current.longPressTimer = null;
        }
        return;
      }
      // 已進入拖拉模式
      e.preventDefault();
      const diffY = e.touches[0].clientY - touchState.current.startY;
      if (touchState.current.clone) {
        touchState.current.clone.style.top = (touchState.current.cloneTop + diffY) + 'px';
      }
      const touchY = e.touches[0].clientY;
      let overId = null;
      document.querySelectorAll('[data-student-id]').forEach(card => {
        const r = card.getBoundingClientRect();
        if (touchY >= r.top && touchY <= r.bottom) overId = card.getAttribute('data-student-id');
      });
      dragState.current.overId = overId;
    }

    function handleTouchEnd() {
      clearTimeout(touchState.current.longPressTimer);
      touchState.current.longPressTimer = null;
      if (touchState.current.clone) {
        touchState.current.clone.remove();
        touchState.current.clone = null;
      }
      if (!touchState.current.isDragging) {
        // 沒有進入拖拉模式 → 視為點擊
        dragState.current = { dragId: null, overId: null };
        return;
      }
      const { dragId, overId } = dragState.current;
      if (dragId && overId && dragId !== overId) {
        const allIds = allStudentsRef.current.map(x => x.id);
        const from = allIds.indexOf(dragId);
        const to = allIds.indexOf(overId);
        if (from >= 0 && to >= 0) {
          const newOrder = [...allIds];
          newOrder.splice(from, 1);
          newOrder.splice(to, 0, dragId);
          onReorderRef.current(newOrder);
        }
      }
      dragState.current = { dragId: null, overId: null };
      touchState.current.isDragging = false;
    }

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);

    return () => {
      clearTimeout(touchState.current.longPressTimer);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [student.id]);

  return (
    <div
      ref={cardRef}
      data-student-id={student.id}
      draggable
      onDragStart={() => { dragState.current.dragId = student.id; }}
      onDragOver={e => { e.preventDefault(); dragState.current.overId = student.id; }}
      onDrop={() => {
        const { dragId, overId } = dragState.current;
        if (!dragId || !overId || dragId === overId) return;
        const allIds = allStudentsRef.current.map(x => x.id);
        const from = allIds.indexOf(dragId);
        const to = allIds.indexOf(overId);
        if (from >= 0 && to >= 0) {
          const newOrder = [...allIds];
          newOrder.splice(from, 1);
          newOrder.splice(to, 0, dragId);
          onReorderRef.current(newOrder);
        }
        dragState.current = { dragId: null, overId: null };
      }}
      style={{ cursor: 'grab', touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      <StudentCard
        student={student}
        remaining={remaining}
        balance={balance}
        used={used}
        purchased={purchased}
        overdue={overdue}
        active={active}
        onClick={onClick}
      />
    </div>
  );
}

// 學員卡片
function StudentCard({ student, remaining, balance, used, purchased, active, onClick, graduated, overdue }) {
  const hasPurchased = (student.payments || []).filter(p => !p.isProxy).length > 0
    || (student.payments || []).some(p => p.isProxy && p.proxyType === 'beneficiary');
  const hasBaseRate = Number(student.baseRate) > 0;
  const baseRate = Number(student.baseRate) || 0;

  let badgeColor, badgeBg, badgeText;
  if (graduated) {
    badgeColor = '#888'; badgeBg = '#f5f5f5'; badgeText = '畢業';
  } else if (!hasPurchased) {
    badgeColor = '#888'; badgeBg = '#f5f5f5'; badgeText = '未設定';
  } else if (hasBaseRate && balance !== null && balance !== undefined) {
    const fmt = formatBalance(balance, baseRate);
    if (fmt.isDebt) {
      badgeColor = '#c5221f'; badgeBg = '#fce8e6';
      badgeText = fmt.remainder > 0
        ? `欠 ${Math.abs(fmt.lessons)} 堂 $${fmt.remainder.toLocaleString()}`
        : `欠 ${Math.abs(fmt.lessons)} 堂`;
    } else if (fmt.lessons === 0 && fmt.remainder > 0) {
      badgeColor = '#b46309'; badgeBg = '#fef9e7';
      badgeText = `餘 $${fmt.remainder.toLocaleString()}`;
    } else {
      badgeColor = fmt.lessons <= 2 ? '#b46309' : '#137333';
      badgeBg = fmt.lessons <= 2 ? '#fef9e7' : '#e6f4ea';
      badgeText = fmt.remainder > 0
        ? `${fmt.lessons} 堂 +$${fmt.remainder.toLocaleString()}`
        : `${fmt.lessons} 堂`;
    }
  } else {
    const r = remaining;
    badgeColor = r < 0 ? '#7b1fa2' : r === 0 ? '#c5221f' : r <= 2 ? '#b46309' : '#137333';
    badgeBg = r < 0 ? '#f3e8fd' : r === 0 ? '#fce8e6' : r <= 2 ? '#fef9e7' : '#e6f4ea';
    badgeText = r < 0 ? `欠 ${Math.abs(r)} 堂` : r === 0 ? '已用完' : `剩 ${r}`;
  }

  return (
    <div
      style={{
        ...styles.studentCard,
        ...(active ? styles.studentCardActive : {}),
        ...(graduated ? { opacity: 0.45 } : {}),
        ...(overdue && !graduated ? { borderLeft: '3px solid #e53935', paddingLeft: 6 } : {}),
      }}
      onClick={onClick}
    >
      <div style={{ ...styles.avatar, background: overdue ? '#fce8e6' : '#e0e0e0', color: overdue ? '#e53935' : '#888' }}>
        {student.name.slice(-2)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: overdue ? '#c5221f' : '#222', display: 'flex', alignItems: 'center', gap: 4 }}>
          {student.name}
          {student.hasTrial && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 10, background: '#EEEDFE', color: '#3C3489' }}>體驗</span>}
          {overdue && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 10, background: '#fce8e6', color: '#c5221f' }}>催費</span>}
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
          {hasPurchased ? `${used} 堂已上` : '尚無購買紀錄'}
        </div>
      </div>
      <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 20, fontWeight: 500, background: badgeBg, color: badgeColor, flexShrink: 0, maxWidth: 80, textAlign: 'right', lineHeight: 1.4 }}>
        {badgeText}
      </span>
    </div>
  );
}

// 學員詳細視窗
function LessonBadge({ lesson, coachColorMap, calendarCoachMap, calendarList }) {
  let hex = '#aaa', name = '未知';
  if (lesson.calendarId && calendarCoachMap?.[lesson.calendarId]) {
    name = calendarCoachMap[lesson.calendarId];
    const cal = (calendarList || []).find(c => c.id === lesson.calendarId);
    hex = cal?.backgroundColor || '#aaa';
  } else if (lesson.colorId) {
    hex = COLOR_MAP[lesson.colorId]?.hex || '#aaa';
    name = coachColorMap[lesson.colorId] || '未知';
  }
  if (name === '未知') return <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: '#eee', color: '#888' }}>未知</span>;
  return (
    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500, background: hex + '28', color: hex, border: `0.5px solid ${hex}55` }}>
      {name}
    </span>
  );
}

function StudentModal({ student, tab, setTab, used, purchased, perLesson, balance, lessons, loadingLessons, coachColorMap, calendarCoachMap, calendarList, allStudents, proxyTargetLessons, onFetchProxyLessons, onUpdate, onAddPayment, onDeletePayment, onUpdatePayment, onDeleteStudent, onAddProxyLesson, onClose }) {
  const [newPayDate, setNewPayDate] = useState('');
  const [newPayLessons, setNewPayLessons] = useState('');
  const [newPayAmount, setNewPayAmount] = useState('');
  const [newPayNotes, setNewPayNotes] = useState('');
  // 代付 UI 狀態
  const [showProxyForm, setShowProxyForm] = useState(false);
  const [proxyBeneficiaryId, setProxyBeneficiaryId] = useState('');
  const [proxyDate, setProxyDate] = useState('');
  const [proxyNotes, setProxyNotes] = useState('');
  const [proxyError, setProxyError] = useState('');
  const [proxySearch, setProxySearch] = useState(''); // ★ 代付搜尋
  const [proxySearchOpen, setProxySearchOpen] = useState(false); // 下拉是否展開
  const [proxySelectedLessons, setProxySelectedLessons] = useState({}); // { lessonKey: true }
  // baseRate 暫存
  const [draftBaseRate, setDraftBaseRate] = useState(student.baseRate || '');
  const [baseRateSaved, setBaseRateSaved] = useState(false);
  // ★ 需求1：編輯付款紀錄
  const [editingPayment, setEditingPayment] = useState(null);
  const [proxyDetailTarget, setProxyDetailTarget] = useState(null);

  // student.baseRate 從外部更新時同步 draft（例如還原備份後）
  const prevBaseRateRef = React.useRef(student.baseRate);
  React.useEffect(() => {
    if (prevBaseRateRef.current !== student.baseRate) {
      setDraftBaseRate(student.baseRate || '');
      prevBaseRateRef.current = student.baseRate;
    }
  }, [student.baseRate]);

  function confirmBaseRate() {
    const val = draftBaseRate === '' ? 0 : parseInt(draftBaseRate);
    onUpdate({ baseRate: isNaN(val) ? 0 : val });
    setBaseRateSaved(true);
    setTimeout(() => setBaseRateSaved(false), 2000);
  }

  const remaining = purchased - used;
  const rColor = remaining <= 0 ? '#c5221f' : remaining <= 2 ? '#b46309' : '#222';
  const totalAmount = (student.payments || []).filter(p => !p.isProxy).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const currentMonthUsed = lessons.filter(l => !l.isTrial && parseInt(l.date.split('/')[1]) === today.getMonth() + 1).length;

  // Credit-Based System 計算
  const baseRate = Number(student.baseRate) || 0;
  const hasBaseRate = baseRate > 0;
  const displayBalance = balance !== null && balance !== undefined ? balance : null;
  // ★ 格式化餘額（N 堂餘 $X）
  const balanceFmt = (hasBaseRate && displayBalance !== null) ? formatBalance(displayBalance, baseRate) : null;
  const balanceColor = displayBalance !== null
    ? (displayBalance < 0 ? '#c5221f' : displayBalance < baseRate * 3 ? '#b46309' : '#188038')
    : rColor;

  function submitPayment() {
    if (!newPayLessons || !newPayAmount) return;
    onAddPayment({
      date: newPayDate,
      lessons: parseInt(newPayLessons),
      amount: parseInt(newPayAmount),
      notes: newPayNotes,
    });
    setNewPayDate(''); setNewPayLessons(''); setNewPayAmount(''); setNewPayNotes('');
  }

  // 代付送出（支援多堂勾選）
  function submitProxy() {
    setProxyError('');
    if (!proxyBeneficiaryId) { setProxyError('請選擇被代付人'); return; }
    const beneficiary = allStudents.find(s => s.id === proxyBeneficiaryId);
    if (!beneficiary) { setProxyError('找不到學員'); return; }
    const bRate = Number(beneficiary.baseRate) || 0;
    if (bRate === 0) { setProxyError(`${beneficiary.name} 尚未設定單堂定價`); return; }

    const selectedKeys = Object.keys(proxySelectedLessons).filter(k => proxySelectedLessons[k]);

    if (selectedKeys.length > 0) {
      // 多堂代付
      selectedKeys.forEach(dateStr => {
        onAddProxyLesson(proxyBeneficiaryId, bRate, { date: dateStr, notes: proxyNotes });
      });
    } else {
      // 無勾選時，用手動填的日期（相容舊行為）
      onAddProxyLesson(proxyBeneficiaryId, bRate, { date: proxyDate, notes: proxyNotes });
    }

    setShowProxyForm(false);
    setProxyBeneficiaryId(''); setProxyDate(''); setProxyNotes(''); setProxyError('');
    setProxySearch(''); setProxySearchOpen(false); setProxySelectedLessons({});
  }

  // 可選代付對象（排除自己）
  const proxyTargets = (allStudents || []).filter(s => s.id !== student.id && !s.graduated);

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* ★ 需求1：編輯付款紀錄 Modal */}
        {editingPayment && (
          <EditPaymentModal
            payment={editingPayment}
            onSave={updates => {
              onUpdatePayment(editingPayment.id, updates);
              setEditingPayment(null);
            }}
            onClose={() => setEditingPayment(null)}
          />
        )}
        {/* Header */}
        <div style={styles.mHead}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ ...styles.avatar, width: 34, height: 34, fontSize: 12, background: '#e0e0e0', color: '#888' }}>
              {student.name.slice(-2)}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500 }}>{student.name}</div>
              {/* 顯示餘額（Credit-Based 或舊制） */}
              {balanceFmt ? (
                <div style={{ fontSize: 12, color: balanceColor, fontWeight: 500 }}>
                  {balanceFmt.isDebt
                    ? `欠費 $${Math.abs(displayBalance).toLocaleString()}（約 ${Math.abs(balanceFmt.lessons)} 堂）`
                    : balanceFmt.remainder > 0
                      ? `剩 ${balanceFmt.lessons} 堂　餘 $${balanceFmt.remainder.toLocaleString()}`
                      : `剩 ${balanceFmt.lessons} 堂`
                  }
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#888' }}>剩餘 {remaining} 堂</div>
              )}
            </div>
          </div>
          <button style={{ background: 'none', border: 'none', fontSize: 20, color: '#888' }} onClick={onClose}>×</button>
        </div>

        <div style={styles.mTabs}>
          {[['info','基本資料'],['pay','付款紀錄'],['hist','上課紀錄']].map(([id, label]) => (
            <div key={id} style={{ ...styles.mTab, ...(tab === id ? styles.mTabActive : {}) }} onClick={() => setTab(id)}>{label}</div>
          ))}
        </div>

        <div style={{ padding: 18, maxHeight: 440, overflowY: 'auto' }}>
          {tab === 'info' && (
            <div>
              {/* 統計卡片 */}
              {hasBaseRate && displayBalance !== null ? (
                <div style={{ background: balanceColor === '#c5221f' ? '#fce8e6' : balanceColor === '#b46309' ? '#fef9e7' : '#e6f4ea', borderRadius: 10, padding: '14px 16px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 11, color: balanceColor, fontWeight: 500, marginBottom: 4 }}>
                      {balanceFmt?.isDebt ? '⚠ 欠費' : '剩餘額度'}
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 600, color: balanceColor, lineHeight: 1 }}>
                      {balanceFmt?.isDebt ? `-$${Math.abs(displayBalance).toLocaleString()}` : `$${displayBalance.toLocaleString()}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>預估剩餘</div>
                    <div style={{ fontSize: 22, fontWeight: 600, color: balanceColor }}>
                      {balanceFmt ? (
                        balanceFmt.isDebt
                          ? `${balanceFmt.lessons} 堂`
                          : balanceFmt.remainder > 0
                            ? `${balanceFmt.lessons} 堂 +$${balanceFmt.remainder.toLocaleString()}`
                            : `${balanceFmt.lessons} 堂`
                      ) : `${remaining} 堂`}
                    </div>
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>單堂定價 ${baseRate.toLocaleString()}</div>
                  </div>
                </div>
              ) : (
                <div style={styles.statsRow}>
                  <div style={styles.statCard}><div style={styles.statLabel}>已上堂數</div><div style={styles.statValue}>{used}</div></div>
                  <div style={styles.statCard}><div style={styles.statLabel}>購買堂數</div><div style={styles.statValue}>{purchased}</div></div>
                  <div style={styles.statCard}><div style={styles.statLabel}>剩餘堂數</div><div style={{ ...styles.statValue, color: rColor }}>{remaining}</div></div>
                </div>
              )}

              {student.hasTrial && (
                <div style={{ background: '#EEEDFE', border: '0.5px solid #AFA9EC', borderRadius: 8, padding: '9px 12px', marginBottom: 10, fontSize: 12, color: '#3C3489' }}>
                  體驗課　{student.trialDate}　$800（與正式課程分開計算）
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f8f8f8', borderRadius: 8, marginBottom: 10, cursor: 'pointer' }}
                onClick={() => onUpdate({ graduated: !student.graduated })}>
                <div style={{ width: 17, height: 17, border: '0.5px solid #999', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', background: student.graduated ? '#222' : '#fff', flexShrink: 0 }}>
                  {student.graduated && <div style={{ width: 9, height: 6, borderLeft: '2px solid #fff', borderBottom: '2px solid #fff', transform: 'rotate(-45deg) translate(1px,-1px)' }} />}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>標記為已畢業</div>
                  <div style={{ fontSize: 11, color: '#888' }}>勾選後移至名單最下層，資料保留</div>
                </div>
              </div>
              {[
                ['姓名', 'name', student.name],
                ['性別', 'gender', student.gender],
                ['生日', 'birthday', student.birthday],
                ['電話', 'phone', student.phone],
              ].map(([label, key, val]) => (
                <div key={key} style={styles.fieldRow}>
                  <label style={styles.fieldLabel}>{label}</label>
                  <input style={styles.fieldInput} value={val || ''} onChange={e => onUpdate({ [key]: e.target.value })} />
                </div>
              ))}
              {/* ★ 需求2：單堂定價 — 暫存模式，按確認才寫入 KV */}
              <div style={styles.fieldRow}>
                <label style={styles.fieldLabel}>單堂定價</label>
                <div style={{ flex: 1, display: 'flex', gap: 6 }}>
                  <input
                    type="number"
                    style={{ ...styles.fieldInput, flex: 1 }}
                    placeholder="例：1000 或 1600（留空使用舊制）"
                    value={draftBaseRate}
                    onChange={e => { setDraftBaseRate(e.target.value); setBaseRateSaved(false); }}
                    onKeyDown={e => { if (e.key === 'Enter') confirmBaseRate(); }}
                  />
                  <button
                    style={{
                      padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500,
                      background: baseRateSaved ? '#e6f4ea' : '#222',
                      color: baseRateSaved ? '#188038' : '#fff',
                      border: 'none', cursor: 'pointer', flexShrink: 0,
                      transition: 'background 0.2s',
                    }}
                    onClick={confirmBaseRate}
                  >
                    {baseRateSaved ? '✓ 已儲存' : '確認'}
                  </button>
                </div>
              </div>
              <div style={styles.fieldRow}>
                <label style={styles.fieldLabel}>備註</label>
                <input style={{ ...styles.fieldInput, flex: 1 }} value={student.notes || ''} onChange={e => onUpdate({ notes: e.target.value })} />
              </div>
            </div>
          )}

          {tab === 'pay' && (
            <div>
              {/* 購課總覽 */}
              {hasBaseRate && displayBalance !== null ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                  <div style={styles.statCard}>
                    <div style={styles.statLabel}>總繳費金額</div>
                    <div style={{ fontSize: 20, fontWeight: 500 }}>${totalAmount.toLocaleString()}</div>
                  </div>
                  <div style={{ ...styles.statCard, background: balanceColor === '#c5221f' ? '#fce8e6' : balanceColor === '#b46309' ? '#fef9e7' : '#e6f4ea' }}>
                    <div style={styles.statLabel}>剩餘餘額 / 預估堂數</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: balanceColor }}>
                      {balanceFmt?.isDebt
                        ? `-$${Math.abs(displayBalance).toLocaleString()} (${balanceFmt.lessons} 堂)`
                        : balanceFmt?.remainder > 0
                          ? `$${displayBalance.toLocaleString()} (${balanceFmt.lessons} 堂餘 $${balanceFmt.remainder.toLocaleString()})`
                          : `$${displayBalance.toLocaleString()} (${balanceFmt?.lessons ?? 0} 堂)`
                      }
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 10 }}>
                  <div style={styles.statCard}><div style={styles.statLabel}>購課總堂數</div><div style={{ fontSize: 20, fontWeight: 500 }}>{purchased}</div></div>
                  <div style={styles.statCard}><div style={styles.statLabel}>已上堂數</div><div style={{ fontSize: 20, fontWeight: 500 }}>{used}</div></div>
                  <div style={styles.statCard}><div style={styles.statLabel}>剩餘堂數</div><div style={{ fontSize: 20, fontWeight: 500, color: remaining <= 0 ? '#c5221f' : remaining <= 3 ? '#b46309' : '#188038' }}>{remaining}</div></div>
                </div>
              )}

              {/* 本月消化 */}
              <div style={{ ...styles.statCard, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#666', marginBottom: 8 }}>本月消化統計</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 500 }}>{currentMonthUsed}</div><div style={{ fontSize: 11, color: '#888' }}>已上堂數</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 500 }}>${((currentMonthUsed * (hasBaseRate ? baseRate : perLesson))).toLocaleString()}</div><div style={{ fontSize: 11, color: '#888' }}>消化金額</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 500 }}>${(hasBaseRate ? baseRate : perLesson).toLocaleString()}</div><div style={{ fontSize: 11, color: '#888' }}>{hasBaseRate ? '單堂定價' : '單堂均價'}</div></div>
                </div>
              </div>

              {/* ★ 額外2：新增付款紀錄（移到列表上方） */}
              <div style={{ background: '#f8f8f8', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#666', marginBottom: 8 }}>新增付款紀錄</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div><label style={styles.fieldLabel}>付款日期</label><input type="date" style={styles.fieldInput} value={newPayDate} onChange={e => setNewPayDate(e.target.value)} /></div>
                  <div><label style={styles.fieldLabel}>購買堂數</label><input type="number" style={styles.fieldInput} placeholder="10" value={newPayLessons} onChange={e => setNewPayLessons(e.target.value)} /></div>
                  <div><label style={styles.fieldLabel}>金額</label><input type="number" style={styles.fieldInput} placeholder="5000" value={newPayAmount} onChange={e => setNewPayAmount(e.target.value)} /></div>
                  <div><label style={styles.fieldLabel}>單堂（自動）</label><input style={{ ...styles.fieldInput, background: '#f0f0f0', color: '#888' }} readOnly value={newPayLessons && newPayAmount ? `$${Math.round(parseInt(newPayAmount) / parseInt(newPayLessons)).toLocaleString()}` : '—'} /></div>
                </div>
                <button style={{ padding: '6px 14px', borderRadius: 8, background: '#222', color: '#fff', border: 'none', fontSize: 12, fontWeight: 500 }} onClick={submitPayment}>新增並儲存</button>
              </div>

              {/* ★ 額外2：代付他人課程（移到列表上方） */}
              <div style={{ marginBottom: 10 }}>
                <button
                  style={{ fontSize: 12, color: '#7b1fa2', background: 'none', border: '0.5px solid #ce93d8', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', width: '100%' }}
                  onClick={() => setShowProxyForm(v => !v)}
                >
                  {showProxyForm ? '▲ 取消代付' : '＋ 代付他人課程'}
                </button>
                {showProxyForm && (
                  <div style={{ background: '#fdf6ff', borderRadius: 8, padding: 12, marginTop: 8, border: '0.5px solid #e1bee7' }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#7b1fa2', marginBottom: 10 }}>
                      由 {student.name} 代付他人課程費用
                      {hasBaseRate && displayBalance !== null && (
                        <span style={{ fontSize: 11, color: '#888', fontWeight: 400, marginLeft: 8 }}>
                          （目前餘額 ${displayBalance.toLocaleString()}）
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <div>
                        <label style={styles.fieldLabel}>被代付學員</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            style={{ ...styles.fieldInput, width: '100%' }}
                            placeholder="輸入姓名搜尋…"
                            value={proxySearch}
                            onChange={e => {
                              setProxySearch(e.target.value);
                              setProxyBeneficiaryId('');
                              setProxySearchOpen(true);
                              setProxySelectedLessons({});
                            }}
                            onFocus={() => setProxySearchOpen(true)}
                          />
                          {proxyBeneficiaryId && (() => {
                            const sel = allStudents.find(s => s.id === proxyBeneficiaryId);
                            return sel ? (
                              <div style={{ fontSize: 11, color: '#188038', marginTop: 3 }}>
                                ✓ {sel.name}{Number(sel.baseRate) ? ` ($${Number(sel.baseRate).toLocaleString()}/堂)` : ''}
                              </div>
                            ) : null;
                          })()}
                          {proxySearchOpen && proxySearch && !proxyBeneficiaryId && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, right: 0,
                              background: '#fff', border: '0.5px solid #ccc', borderRadius: 8,
                              zIndex: 200, maxHeight: 180, overflowY: 'auto',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}>
                              {proxyTargets
                                .filter(s => s.name.includes(proxySearch))
                                .map(s => (
                                  <div
                                    key={s.id}
                                    style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '0.5px solid #f0f0f0' }}
                                    onMouseDown={() => {
                                      setProxyBeneficiaryId(s.id);
                                      setProxySearch(s.name);
                                      setProxySearchOpen(false);
                                      setProxySelectedLessons({});
                                      // 抓取被代付人的上課紀錄
                                      if (onFetchProxyLessons) onFetchProxyLessons(s.id, s.name);
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                  >
                                    {s.name}
                                    {Number(s.baseRate) ? <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>${Number(s.baseRate).toLocaleString()}/堂</span> : <span style={{ fontSize: 11, color: '#aaa', marginLeft: 6 }}>未設定定價</span>}
                                  </div>
                                ))
                              }
                              {proxyTargets.filter(s => s.name.includes(proxySearch)).length === 0 && (
                                <div style={{ padding: '10px 12px', fontSize: 13, color: '#aaa' }}>找不到符合的學員</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label style={styles.fieldLabel}>上課日期</label>
                        <input type="date" style={styles.fieldInput} value={proxyDate} onChange={e => setProxyDate(e.target.value)} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={styles.fieldLabel}>備註</label>
                      <input style={{ ...styles.fieldInput, width: '100%' }} placeholder="（選填）" value={proxyNotes} onChange={e => setProxyNotes(e.target.value)} />
                    </div>
                    {proxyError && (
                      <div style={{ fontSize: 12, color: '#c5221f', background: '#fce8e6', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>
                        ⚠ {proxyError}
                      </div>
                    )}
                    {proxyBeneficiaryId && (() => {
                      const b = allStudents.find(s => s.id === proxyBeneficiaryId);
                      const bRate = Number(b?.baseRate) || 0;

                      // 找被代付人的未付款上課紀錄（從 allStudents 找不到 lessons，需要從 App 層傳入）
                      // 這裡用 proxyTargetLessons prop 或直接用全局 allEvents 計算
                      const bPaidDates = new Set(
                        (b?.payments || [])
                          .filter(p => p.quickPay || (p.isProxy && p.proxyType === 'beneficiary'))
                          .map(p => p.date || p.lessonDate)
                          .filter(Boolean)
                      );

                      // 從 allStudents 的 payments 裡找有無代付紀錄
                      const bUnpaidLessons = (proxyTargetLessons?.[proxyBeneficiaryId] || [])
                        .filter(l => {
                          const p = l.date.split('/');
                          const iso = `${p[0]}-${String(p[1]).padStart(2,'0')}-${String(p[2]).padStart(2,'0')}`;
                          return !bPaidDates.has(iso);
                        })
                        .sort((a, b) => new Date(a.date.replace(/\//g,'-')) - new Date(b.date.replace(/\//g,'-')));

                      const selectedCount = Object.values(proxySelectedLessons).filter(Boolean).length;

                      return (
                        <>
                          {bRate > 0 && (
                            <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
                              將從 {student.name} 餘額扣除 <strong>${bRate.toLocaleString()}</strong>（{b?.name} 的單堂定價）
                            </div>
                          )}
                          {bUnpaidLessons.length > 0 && (
                            <div style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 11, fontWeight: 500, color: '#7b1fa2', marginBottom: 6 }}>
                                {b?.name} 未付款上課紀錄（勾選要代付的堂數）
                              </div>
                              <div style={{ maxHeight: 160, overflowY: 'auto', border: '0.5px solid #e1bee7', borderRadius: 8, padding: '6px 8px' }}>
                                {bUnpaidLessons.map((l, j) => {
                                  const p = l.date.split('/');
                                  const iso = `${p[0]}-${String(p[1]).padStart(2,'0')}-${String(p[2]).padStart(2,'0')}`;
                                  const checked = !!proxySelectedLessons[iso];
                                  return (
                                    <div key={j}
                                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', cursor: 'pointer', borderRadius: 4, background: checked ? '#f3e8fd' : 'transparent' }}
                                      onClick={() => setProxySelectedLessons(prev => ({ ...prev, [iso]: !prev[iso] }))}
                                    >
                                      <div style={{ width: 14, height: 14, border: `0.5px solid ${checked ? '#7b1fa2' : '#ccc'}`, borderRadius: 3, background: checked ? '#7b1fa2' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        {checked && <div style={{ width: 7, height: 4, borderLeft: '1.5px solid #fff', borderBottom: '1.5px solid #fff', transform: 'rotate(-45deg) translate(1px,-1px)' }} />}
                                      </div>
                                      <span style={{ fontSize: 12, color: '#444' }}>{l.date}</span>
                                      {l.time && <span style={{ fontSize: 11, color: '#aaa' }}>{l.time}</span>}
                                      <span style={{ fontSize: 11, color: '#7b1fa2', marginLeft: 'auto' }}>${bRate.toLocaleString()}</span>
                                    </div>
                                  );
                                })}
                              </div>
                              {selectedCount > 0 && (
                                <div style={{ fontSize: 11, color: '#7b1fa2', marginTop: 4 }}>
                                  已選 {selectedCount} 堂，共 ${(selectedCount * bRate).toLocaleString()}
                                </div>
                              )}
                            </div>
                          )}
                          {bUnpaidLessons.length === 0 && bRate > 0 && (
                            <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8 }}>
                              （無未付款上課紀錄，請手動填入日期）
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <button
                      style={{ padding: '6px 14px', borderRadius: 8, background: '#7b1fa2', color: '#fff', border: 'none', fontSize: 12, fontWeight: 500 }}
                      onClick={submitProxy}
                    >確認代付</button>
                  </div>
                )}
              </div>

              {/* 付款紀錄列表：左右分欄 */}
              {(() => {
                const ownPayments = (student.payments || [])
                  .filter(p => !p.isProxy)
                  .sort((a, b) => (a.date ? new Date(a.date) : new Date(0)) - (b.date ? new Date(b.date) : new Date(0)));

                const payerPayments = (student.payments || [])
                  .filter(p => p.isProxy && p.proxyType === 'payer')
                  .sort((a, b) => (a.date ? new Date(a.date) : new Date(0)) - (b.date ? new Date(b.date) : new Date(0)));

                const beneficiaryPayments = (student.payments || [])
                  .filter(p => p.isProxy && p.proxyType === 'beneficiary')
                  .sort((a, b) => (a.date ? new Date(a.date) : new Date(0)) - (b.date ? new Date(b.date) : new Date(0)));

                // 被代付等效堂數
                const proxyReceivedLessons = baseRate > 0
                  ? beneficiaryPayments.reduce((sum, p) => sum + Math.round((Number(p.proxyAmount) || 0) / baseRate), 0)
                  : 0;

                // 代付他人：依被代付人名稱分組，取得每人的詳細紀錄
                const payerGroupMap = {};
                payerPayments.forEach(p => {
                  // 從 notes 裡抓被代付人名字（格式：「代付 XXX（$Y）」）
                  const match = p.notes?.match(/代付\s+(.+?)（/);
                  const name = match ? match[1] : '未知';
                  if (!payerGroupMap[name]) payerGroupMap[name] = [];
                  payerGroupMap[name].push(p);
                });

                // 代付彈跳視窗 state（局部）

                const hasOwn = ownPayments.length > 0;
                const hasPayer = payerPayments.length > 0;

                return (
                  <>
                    {/* 彈跳視窗：代付詳細 */}
                    {proxyDetailTarget && (
                      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 700 }}
                        onClick={() => setProxyDetailTarget(null)}>
                        <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: 320, maxWidth: '95vw' }}
                          onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                            <span style={{ fontSize: 14, fontWeight: 500 }}>代付 {proxyDetailTarget.name} 的紀錄</span>
                            <button style={{ background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer' }} onClick={() => setProxyDetailTarget(null)}>×</button>
                          </div>
                          {proxyDetailTarget.records.map((p, j) => (
                            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '0.5px solid #eee', fontSize: 13 }}>
                              <span style={{ color: '#888', flex: 1 }}>{p.date || '未填日期'}</span>
                              <span style={{ fontWeight: 500, color: '#b46309' }}>-${Number(p.proxyAmount).toLocaleString()}</span>
                              <button onClick={() => setEditingPayment(p)} style={{ background: 'none', border: 'none', color: '#bbb', fontSize: 12, cursor: 'pointer', padding: '0 2px' }}>✎</button>
                              <button onClick={() => {
                                if (window.confirm('確定刪除此筆代付紀錄？\n對應的被代付紀錄也會一併刪除。')) {
                                  onDeletePayment(p.id);
                                  const remaining = proxyDetailTarget.records.filter(r => r.id !== p.id);
                                  if (remaining.length === 0) setProxyDetailTarget(null);
                                  else setProxyDetailTarget({ ...proxyDetailTarget, records: remaining });
                                }
                              }} style={{ background: 'none', border: 'none', color: '#e53935', fontSize: 13, cursor: 'pointer', padding: '0 2px' }}>×</button>
                            </div>
                          ))}
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0 4px', fontSize: 13, fontWeight: 500, borderTop: '0.5px solid #ccc', marginTop: 4 }}>
                            <span>合計 {proxyDetailTarget.records.length} 筆</span>
                            <span style={{ color: '#b46309' }}>-${proxyDetailTarget.records.reduce((s, p) => s + (Number(p.proxyAmount) || 0), 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: hasPayer ? '1fr 1fr' : '1fr', gap: 12 }}>
                      {/* 左側：自己的付款紀錄 */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 500, color: '#aaa', marginBottom: 6 }}>自己的付款紀錄</div>
                        {!hasOwn && (
                          <div style={{ fontSize: 12, color: '#ccc', padding: '8px 0' }}>（無）</div>
                        )}
                        {ownPayments.map((p, i) => (
                          <div key={p.id || i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: '0.5px solid #eee', fontSize: 12 }}>
                            <span style={{ color: '#888', flexShrink: 0, width: 80 }}>{p.date || '未填日期'}</span>
                            <span style={{ flex: 1, color: '#444' }}>{p.lessons > 0 ? `${p.lessons}堂` : ''} ${(p.amount || 0).toLocaleString()}</span>
                            <button onClick={() => setEditingPayment(p)} style={{ background: 'none', border: 'none', color: '#bbb', fontSize: 12, cursor: 'pointer', padding: '0 2px' }}>✎</button>
                            <button onClick={() => { if (window.confirm('確定刪除此筆紀錄？')) onDeletePayment(p.id); }} style={{ background: 'none', border: 'none', color: '#e53935', fontSize: 13, cursor: 'pointer', padding: '0 2px' }}>×</button>
                          </div>
                        ))}
                        {/* 被代付紀錄（被他人代付） */}
                        {beneficiaryPayments.length > 0 && (
                          <>
                            <div style={{ fontSize: 11, fontWeight: 500, color: '#7b1fa2', marginTop: 8, marginBottom: 4 }}>被代付紀錄</div>
                            {beneficiaryPayments.map((p, i) => (
                              <div key={p.id || i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', borderBottom: '0.5px solid #eee', fontSize: 12, opacity: 0.8 }}>
                                <span style={{ color: '#888', flexShrink: 0, width: 80 }}>{p.date || '未填日期'}</span>
                                <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 20, background: '#f3e8fd', color: '#7b1fa2', border: '0.5px solid #ce93d8', flexShrink: 0 }}>被代付</span>
                                <span style={{ flex: 1, fontSize: 11, color: '#888' }}>${Number(p.proxyAmount).toLocaleString()}</span>
                                <button onClick={() => { if (window.confirm('確定刪除此筆被代付紀錄？\n對應的代付紀錄也會一併刪除。')) onDeletePayment(p.id); }} style={{ background: 'none', border: 'none', color: '#e53935', fontSize: 13, cursor: 'pointer', padding: '0 2px' }}>×</button>
                              </div>
                            ))}
                            {/* 被代付、自付、合計 */}
                            <div style={{ fontSize: 12, padding: '8px 0 4px', borderTop: '0.5px solid #eee', marginTop: 4 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#555', marginBottom: 3 }}>
                                <span>自付總金額</span>
                                <span>${ownPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0).toLocaleString()}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#7b1fa2', marginBottom: 3 }}>
                                <span>被代付總金額</span>
                                <span>${beneficiaryPayments.reduce((s, p) => s + (Number(p.proxyAmount) || 0), 0).toLocaleString()}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500, color: '#188038', borderTop: '0.5px solid #eee', paddingTop: 4 }}>
                                <span>合計</span>
                                <span>${(ownPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0) + beneficiaryPayments.reduce((s, p) => s + (Number(p.proxyAmount) || 0), 0)).toLocaleString()}</span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* 右側：代付他人紀錄（依被代付人分組） */}
                      {hasPayer && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 500, color: '#aaa', marginBottom: 6 }}>代付他人紀錄</div>
                          {Object.entries(payerGroupMap).map(([name, records]) => (
                            <div key={name}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid #eee', cursor: 'pointer' }}
                              onClick={() => setProxyDetailTarget({ name, records })}
                            >
                              <span style={{ fontSize: 13, color: '#7b1fa2', fontWeight: 500 }}>{name}</span>
                              <span style={{ fontSize: 12, color: '#aaa' }}>{records.length} 筆 →</span>
                            </div>
                          ))}
                          <div style={{ fontSize: 11, color: '#b46309', padding: '6px 0', borderTop: '0.5px solid #eee', marginTop: 4 }}>
                            合計 -${payerPayments.reduce((s, p) => s + (Number(p.proxyAmount) || 0), 0).toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 合計行 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', fontSize: 13, fontWeight: 500, borderTop: '0.5px solid #ccc', borderBottom: '0.5px solid #eee', marginBottom: 8, marginTop: 8 }}>
                      <span>
                        自購 {purchased} 堂
                        {proxyReceivedLessons > 0 && <span style={{ color: '#7b1fa2', marginLeft: 8 }}>+ 被代付 {proxyReceivedLessons} 堂</span>}
                      </span>
                      <span style={{ color: '#222' }}>
                        ${totalAmount.toLocaleString()}
                        {perLesson > 0 && <>　<span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>均價</span><span style={{ color: '#188038', fontWeight: 500 }}> ${perLesson.toLocaleString()}</span><span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>/堂</span></>}
                      </span>
                    </div>
                  </>
                );
              })()}

            </div>
          )}

          {tab === 'hist' && (
            <div>
              {loadingLessons
                ? <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 13, color: '#aaa' }}>載入上課紀錄中...</div>
                : lessons.length === 0
                  ? <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 13, color: '#aaa' }}>尚無上課紀錄</div>
                  : (() => {
                      const groups = {};
                      lessons.forEach(l => {
                        const parts = l.date.split('/');
                        const key = `${parts[0]}年 ${parts[1]}月`;
                        if (!groups[key]) groups[key] = [];
                        groups[key].push(l);
                      });
                      const allRows = Object.values(groups).flat();
                      const sortedAll = [...allRows].sort((a, b) =>
                        new Date(a.date.replace(/\//g,'-')) - new Date(b.date.replace(/\//g,'-'))
                      );
                      const cumMap = new Map();
                      sortedAll.forEach((l, i) => { cumMap.set(l, i + 1); });

                      function toISODate(dateStr) {
                        const p = dateStr.split('/');
                        return `${p[0]}-${String(p[1]).padStart(2,'0')}-${String(p[2]).padStart(2,'0')}`;
                      }

                      // 只看 quickPay 付款紀錄
                      const quickPayments = (student.payments || []).filter(p => p.quickPay);

                      // 一般付款（非 quickPay、非 isProxy）
                      const regularPayments = (student.payments || []).filter(p => !p.quickPay && !p.isProxy && p.date && Number(p.amount) > 0);

                      // 對每堂課找對應的 quickPay 紀錄
                      function getQuickPay(lesson) {
                        const iso = toISODate(lesson.date);
                        return quickPayments.find(p => p.lessonDate === iso || p.date === iso);
                      }

                      // 找每筆一般付款後最近的一堂課（用於標示）
                      const paymentLabels = new Map(); // lessonDate → [payment]
                      regularPayments.forEach(rp => {
                        const rpDate = new Date(rp.date);
                        // 找付款日期之後最近的一堂課
                        const nextLesson = sortedAll.find(l => {
                          const ld = new Date(toISODate(l.date));
                          return ld >= rpDate;
                        });
                        if (nextLesson) {
                          const key = toISODate(nextLesson.date);
                          if (!paymentLabels.has(key)) paymentLabels.set(key, []);
                          paymentLabels.get(key).push(rp);
                        }
                      });

                      function handleQuickPay(lesson) {
                        const iso = toISODate(lesson.date);
                        const existing = getQuickPay(lesson);
                        if (existing) {
                          // 取消：確認後刪除
                          if (window.confirm(`確定取消 ${lesson.date} 的當日付款紀錄？`)) {
                            onDeletePayment(existing.id);
                          }
                        } else {
                          // 新增
                          const rate = Number(student.baseRate) || 0;
                          onAddPayment({
                            date: iso,
                            lessons: 1,
                            amount: rate,
                            notes: `當日結算（${lesson.date}${lesson.time ? ' ' + lesson.time : ''}）`,
                            quickPay: true,
                            lessonDate: iso,
                          });
                        }
                      }

                      return Object.entries(groups).map(([monthKey, rows]) => (
                        <div key={monthKey} style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, fontWeight: 500, color: '#aaa', letterSpacing: '0.05em', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                            <span>{monthKey}</span>
                            <span style={{ color: '#bbb' }}>{rows.length} 堂</span>
                          </div>
                          {rows.map((l, i) => {
                            const qp = getQuickPay(l);
                            const isPaid = !!qp;
                            const iso = toISODate(l.date);
                            const labels = paymentLabels.get(iso) || [];
                            return (
                              <div key={i} style={{ padding: '7px 0', borderBottom: '0.5px solid #eee', fontSize: 13 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {/* 勾選框：只對應 quickPay */}
                                  <div
                                    title={isPaid ? '點擊取消當日付款' : '點擊新增當日付款'}
                                    onClick={() => handleQuickPay(l)}
                                    style={{
                                      width: 16, height: 16,
                                      border: `0.5px solid ${isPaid ? '#188038' : '#ccc'}`,
                                      borderRadius: 4, flexShrink: 0, cursor: 'pointer',
                                      background: isPaid ? '#e6f4ea' : '#fff',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                  >
                                    {isPaid && <div style={{ width: 8, height: 5, borderLeft: '1.5px solid #188038', borderBottom: '1.5px solid #188038', transform: 'rotate(-45deg) translate(1px,-1px)' }} />}
                                  </div>
                                  <span style={{ color: '#888', width: 80 }}>{l.date}</span>
                                  <span style={{ width: 46 }}>{l.time}</span>
                                  <LessonBadge lesson={l} coachColorMap={coachColorMap} calendarCoachMap={calendarCoachMap} calendarList={calendarList} />
                                  <span style={{ fontSize: 11, color: '#bbb', marginLeft: 'auto' }}>第 {cumMap.get(l)} 堂</span>
                                </div>
                                {/* 全款付款標示 */}
                                {labels.map((rp, j) => (
                                  <div key={j} style={{ marginLeft: 24, marginTop: 3, fontSize: 11, color: '#1967d2', background: '#e8f0fe', borderRadius: 4, padding: '2px 8px', display: 'inline-block' }}>
                                    💳 {rp.date} 付款 ${Number(rp.amount).toLocaleString()}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()
              }
              {!loadingLessons && lessons.length > 0 && (
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                  ☑ 點擊方框新增／取消當日付款（${Number(student.baseRate) > 0 ? Number(student.baseRate).toLocaleString() : '請先設定單堂定價'}）
                </div>
              )}
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>* 資料從 Google Calendar 自動同步，共 {lessons.length} 堂</div>
            </div>
          )}
        </div>

        {/* ★ 需求5：移除「儲存至 Google Sheets」按鈕，只保留刪除和關閉 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '12px 18px', borderTop: '0.5px solid #eee' }}>
          <button
            style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, background: 'none', border: '0.5px solid #ffcdd2', color: '#e53935', cursor: 'pointer' }}
            onClick={() => { if (window.confirm(`確定刪除學員「${student.name}」？此動作無法復原。`)) onDeleteStudent(); }}
          >刪除學員</button>
          <button style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#666', cursor: 'pointer' }} onClick={onClose}>關閉</button>
        </div>
      </div>
    </div>
  );
}

function LessonRow({ lesson, coachColorMap }) {
  const getHex = (colorId, cMap) => {
    if (!colorId) return '#aaa';
    if (colorId === '__default__') {
      const def = cMap?.['__default__'];
      const entry = def && Object.entries(cMap).find(([id, n]) => id !== '__default__' && n === def);
      return entry ? (COLOR_MAP[entry[0]]?.hex || '#aaa') : '#aaa';
    }
    return COLOR_MAP[colorId]?.hex || '#aaa';
  };
  const hex = getHex(lesson.colorId, coachColorMap);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '0.5px solid #eee', fontSize: 13 }}>
      <span style={{ color: '#888', width: 80 }}>{lesson.date}</span>
      <span style={{ width: 46 }}>{lesson.time}</span>
      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500, background: hex + '33', color: hex }}>
        {coachColorMap[lesson.colorId] || '未知'}
      </span>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <div style={{ ...styles.tab, ...(active ? styles.tabActive : {}) }} onClick={onClick}>
      {children}
    </div>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, zIndex: 500 };
const modalStyle = { background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 520, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto' };

const styles = {
  topbar: { background: '#fff', borderBottom: '0.5px solid #e0e0e0', padding: '0 16px', display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap' },
  topbarTitle: { fontSize: 15, fontWeight: 500, color: '#222', padding: '13px 0', marginRight: 20 },
  tab: { fontSize: 13, color: '#888', padding: '13px 12px', cursor: 'pointer', borderBottom: '2px solid transparent', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' },
  tabActive: { color: '#222', borderBottom: '2px solid #222', fontWeight: 500 },
  iconBtn: { padding: '6px 12px', borderRadius: 8, background: 'none', border: '0.5px solid #e0e0e0', fontSize: 12, color: '#666' },
  leftPanel: { width: 250, minWidth: 250, background: '#fff', borderRight: '0.5px solid #e0e0e0', padding: 14, maxHeight: 'calc(100vh - 49px)', overflowY: 'auto' },
  panelHeader: { fontSize: 11, fontWeight: 500, color: '#aaa', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 },
  rightPanel: { flex: 1, padding: 14, maxHeight: 'calc(100vh - 49px)', overflowY: 'auto', minWidth: 0, overflow: 'auto' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 },
  statCard: { background: '#f5f5f5', borderRadius: 8, padding: '11px 13px' },
  statLabel: { fontSize: 11, color: '#888', marginBottom: 3 },
  statValue: { fontSize: 21, fontWeight: 500, color: '#222' },
  calHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  navBtn: { background: 'none', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: '4px 9px', fontSize: 12, color: '#666' },
  modeToggle: { display: 'flex', border: '0.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' },
  modeBtn: { fontSize: 12, padding: '4px 10px', cursor: 'pointer', color: '#888', background: 'none', border: 'none' },
  modeBtnActive: { background: '#f5f5f5', color: '#222', fontWeight: 500 },
  legend: { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#666' },
  calWrap: { background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: 12, overflow: 'hidden', marginBottom: 12, width: '100%', minWidth: 0 },
  dowRow: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: '#f8f8f8', borderBottom: '0.5px solid #e0e0e0' },
  dow: { fontSize: 11, color: '#aaa', textAlign: 'center', padding: '5px 0', fontWeight: 500 },
  calCells: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', width: '100%' },
  cell: { minHeight: 'clamp(44px, 10vw, 62px)', padding: 'clamp(1px, 0.5vw, 3px)', borderRight: '0.5px solid #e0e0e0', borderBottom: '0.5px solid #e0e0e0', overflow: 'hidden', minWidth: 0 },
  cellDate: { fontSize: 11, color: '#aaa', marginBottom: 2, padding: '1px 3px', width: 18 },
  cellDateToday: { background: '#222', color: '#fff', borderRadius: '50%', height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 },
  ev: { fontSize: 10, padding: '2px 4px', borderRadius: 3, marginBottom: 2, overflow: 'hidden', whiteSpace: 'nowrap', fontWeight: 500 },
  weekHeader: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: '0.5px solid #e0e0e0' },
  weekDay: { padding: '7px 5px', textAlign: 'center', borderRight: '0.5px solid #e0e0e0' },
  weekDayNum: { fontSize: 18, fontWeight: 500, color: '#222', marginTop: 2 },
  weekBody: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' },
  weekCell: { minHeight: 90, padding: 5, borderRight: '0.5px solid #e0e0e0' },
  exportBtn: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#666' },
  studentCard: { display: 'flex', alignItems: 'center', gap: 9, padding: '8px 9px', borderRadius: 8, cursor: 'pointer', border: '0.5px solid transparent', marginBottom: 2 },
  studentCardActive: { background: '#f5f5f5', borderColor: '#e0e0e0' },
  avatar: { width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, flexShrink: 0 },
  mHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid #eee' },
  mTabs: { display: 'flex', borderBottom: '0.5px solid #eee', padding: '0 18px' },
  mTab: { fontSize: 13, padding: '9px 12px', cursor: 'pointer', color: '#888', borderBottom: '2px solid transparent' },
  mTabActive: { color: '#222', fontWeight: 500, borderBottom: '2px solid #222' },
  fieldRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  fieldLabel: { fontSize: 12, color: '#888', width: 40, flexShrink: 0 },
  fieldInput: { flex: 1, border: '0.5px solid #ccc', borderRadius: 8, padding: '6px 9px', fontSize: 13 },
};

// 從行事曆自動匯入學員確認視窗
function ImportConfirmModal({ accessToken, enabledCalendars, existingStudents, onConfirm, onClose }) {
  const existingNames = new Set(existingStudents.map(s => s.name));
  const [calendarNames, setCalendarNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  // 直接從 Google Calendar 抓取原始名字（不過濾）
  useEffect(() => {
    async function fetchRawNames() {
      setLoading(true);
      try {
        const now = new Date();
        // 抓最近 2 年的資料，確保所有學員都能被偵測到
        const timeMin = new Date(now.getFullYear() - 2, 0, 1).toISOString();
        const timeMax = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

        const activeCalendars = enabledCalendars
          ? Object.entries(enabledCalendars).filter(([,v]) => v).map(([id]) => id)
          : [process.env.REACT_APP_CALENDAR_ID].filter(Boolean);

        const allTitles = new Set();
        await Promise.all(activeCalendars.map(async calId => {
          try {
            const res = await fetch(
              `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calId)}/events` +
              `?timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&maxResults=2500`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            if (!res.ok) return;
            const data = await res.json();
            (data.items || []).forEach(ev => {
              const title = ev.summary || '';
              title.split(/[、,，]/).map(s => s.trim()).filter(Boolean).forEach(n => allTitles.add(n));
            });
          } catch(e) {}
        }));
        setCalendarNames([...allTitles].sort());
      } catch(e) {}
      setLoading(false);
    }
    fetchRawNames();
  }, []);

  // 不過濾，直接列出所有未在學員列表的名字
  const newNames = calendarNames.filter(n => !existingNames.has(n));
  const [selected, setSelected] = useState({});

  // 預設全選新名字
  useEffect(() => {
    const s = {};
    newNames.forEach(n => { s[n] = true; });
    setSelected(s);
  }, [calendarNames.length]);

  const selectedNames = newNames.filter(n => selected[n]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 480, maxWidth: '95vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '0.5px solid #eee' }}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>從行事曆匯入學員</span>
          <button style={{ background: 'none', border: 'none', fontSize: 20, color: '#888' }} onClick={onClose}>×</button>
        </div>
        <div style={{ padding: '16px 20px', maxHeight: 400, overflowY: 'auto' }}>

          {/* 狀態摘要 */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '8px 12px', flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#888' }}>行事曆偵測到</div>
              <div style={{ fontSize: 18, fontWeight: 500 }}>{calendarNames.length} 人</div>
            </div>
            <div style={{ background: '#e6f4ea', borderRadius: 8, padding: '8px 12px', flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#888' }}>尚未建立</div>
              <div style={{ fontSize: 18, fontWeight: 500, color: '#188038' }}>{newNames.length} 人</div>
            </div>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '8px 12px', flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#888' }}>已在列表</div>
              <div style={{ fontSize: 18, fontWeight: 500 }}>{existingNames.size} 人</div>
            </div>
          </div>

          {loading && (
            <div style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: '16px 0' }}>
              正在從行事曆讀取所有名字...
            </div>
          )}

          {newNames.length === 0 && calendarNames.length > 0 ? (
            <div style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: '10px 0 6px' }}>
              行事曆中偵測到的學員都已在列表中。
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, color: '#666', marginBottom: 8, lineHeight: 1.6 }}>
                以下 {newNames.length} 位尚未建立，勾選後點確認新增：
              </p>
              <input
                style={{ width: '100%', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', fontSize: 12, marginBottom: 10 }}
                placeholder="搜尋名字..."
                value={searchFilter}
                onChange={e => setSearchFilter(e.target.value)}
              />
              {newNames.filter(n => !searchFilter || n.includes(searchFilter)).map(name => (
                <div key={name}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', border: '0.5px solid #eee', marginBottom: 6, background: selected[name] ? '#f0faf3' : '#fff' }}
                  onClick={() => setSelected(prev => ({ ...prev, [name]: !prev[name] }))}>
                  <div style={{ width: 16, height: 16, border: '0.5px solid #ccc', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', background: selected[name] ? '#188038' : '#fff', borderColor: selected[name] ? '#188038' : '#ccc', flexShrink: 0 }}>
                    {selected[name] && <div style={{ width: 9, height: 6, borderLeft: '2px solid #fff', borderBottom: '2px solid #fff', transform: 'rotate(-45deg) translate(1px,-1px)' }} />}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{name}</span>
                </div>
              ))}
              {newNames.filter(n => !searchFilter || n.includes(searchFilter)).length === 0 && searchFilter && (
                <div style={{ fontSize: 13, color: '#aaa', textAlign: 'center', padding: '8px 0' }}>沒有符合「{searchFilter}」的名字</div>
              )}
            </>
          )}

          {/* 已在列表的學員（可展開查看） */}
          {existingNames.size > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                style={{ fontSize: 12, color: '#888', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}
                onClick={() => setShowAll(v => !v)}
              >
                {showAll ? '▲ 隱藏已建立的學員' : `▼ 顯示已建立的 ${existingNames.size} 位學員`}
              </button>
              {showAll && (
                <div style={{ padding: '8px 10px', background: '#f8f8f8', borderRadius: 8, fontSize: 12, color: '#888', marginTop: 4 }}>
                  {[...existingNames].join('、')}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '0.5px solid #eee' }}>
          <button style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#666', cursor: 'pointer' }} onClick={onClose}>取消</button>
          {newNames.length > 0 && (
            <button style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, background: '#188038', border: 'none', color: '#fff', fontWeight: 500, cursor: 'pointer' }}
              onClick={() => onConfirm(selectedNames)}>
              匯入 {selectedNames.length} 位學員
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// 收入詳細視窗（今日為止的上課與收費狀況）
function IncomeDetailModal({ events, students, getPerLesson, currentMonth, currentYear, onClose }) {
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];

  // 預設：本月第一天到今天
  const defaultStart = `${currentYear}-${String(currentMonth).padStart(2,'0')}-01`;
  const defaultEnd = todayStr;

  const [rangeStart, setRangeStart] = useState(defaultStart);
  const [rangeEnd, setRangeEnd] = useState(defaultEnd);
  const [expandedStudent, setExpandedStudent] = useState(null);

  // 快速區間按鈕
  const PRESETS = [
    { label: '本月', start: `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`, end: todayStr },
    { label: '上月', start: (() => { const d = new Date(now.getFullYear(), now.getMonth()-1, 1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; })(),
      end: (() => { const d = new Date(now.getFullYear(), now.getMonth(), 0); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })() },
    { label: '本季', start: (() => { const q = Math.floor(now.getMonth()/3); return `${now.getFullYear()}-${String(q*3+1).padStart(2,'0')}-01`; })(), end: todayStr },
    { label: '今年', start: `${now.getFullYear()}-01-01`, end: todayStr },
  ];

  function inRange(dateStr) {
    if (!dateStr) return false;
    const p = dateStr.split('/');
    const d = new Date(parseInt(p[0]), parseInt(p[1])-1, parseInt(p[2]));
    const s = new Date(rangeStart); s.setHours(0,0,0,0);
    const e = new Date(rangeEnd); e.setHours(23,59,59,999);
    return d >= s && d <= e;
  }

  const rangeEvents = events.filter(ev => !ev.isTrial && inRange(ev.date));

  // 依學員分組統計，同時收集上課明細
  const studentStats = students
    .filter(s => !s.graduated)
    .map(s => {
      const lessons = rangeEvents
        .filter(ev => ev.studentName === s.name)
        .sort((a, b) => {
          const da = new Date(a.date.replace(/\//g,'-') + (a.time ? 'T'+a.time : ''));
          const db = new Date(b.date.replace(/\//g,'-') + (b.time ? 'T'+b.time : ''));
          return da - db;
        });
      // ★ 優先用 baseRate（金額制），fallback 到舊制均價
      const baseRate = Number(s.baseRate) || 0;
      const perL = baseRate > 0 ? baseRate : getPerLesson(s);
      return { name: s.name, lessons, used: lessons.length, perL, income: lessons.length * perL };
    })
    .filter(s => s.used > 0)
    .sort((a, b) => b.income - a.income);

  const totalIncome = studentStats.reduce((sum, s) => sum + s.income, 0);
  const totalLessons = studentStats.reduce((sum, s) => sum + s.used, 0);

  // 區間內已收學費（含代付金額）
  const collected = students.reduce((sum, s) => {
    return sum + (s.payments || []).filter(p => {
      if (!p.date || p.isProxy) return false; // 排除代付，非實收
      const d = new Date(p.date); d.setHours(12);
      const s2 = new Date(rangeStart); s2.setHours(0);
      const e = new Date(rangeEnd); e.setHours(23,59,59);
      return d >= s2 && d <= e;
    }).reduce((a, p) => a + (Number(p.amount) || 0), 0);
  }, 0);

  const isPreset = (p) => p.start === rangeStart && p.end === rangeEnd;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
      <div style={{ background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 560, maxWidth: '97vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* 標題 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid #eee', flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>收費明細</span>
          <button style={{ background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer' }} onClick={onClose}>×</button>
        </div>

        {/* 區間選擇 */}
        <div style={{ padding: '12px 18px', borderBottom: '0.5px solid #eee', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {PRESETS.map(p => (
              <button key={p.label}
                style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: '0.5px solid', borderColor: isPreset(p) ? '#222' : '#ccc', background: isPreset(p) ? '#222' : '#fff', color: isPreset(p) ? '#fff' : '#555', fontWeight: isPreset(p) ? 500 : 400 }}
                onClick={() => { setRangeStart(p.start); setRangeEnd(p.end); setExpandedStudent(null); }}
              >{p.label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="date" style={{ border: '0.5px solid #ccc', borderRadius: 8, padding: '5px 8px', fontSize: 13, flex: 1 }}
              value={rangeStart} max={rangeEnd}
              onChange={e => { setRangeStart(e.target.value); setExpandedStudent(null); }} />
            <span style={{ color: '#aaa', fontSize: 13 }}>→</span>
            <input type="date" style={{ border: '0.5px solid #ccc', borderRadius: 8, padding: '5px 8px', fontSize: 13, flex: 1 }}
              value={rangeEnd} min={rangeStart} max={todayStr}
              onChange={e => { setRangeEnd(e.target.value); setExpandedStudent(null); }} />
          </div>
        </div>

        {/* 摘要卡片 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '12px 18px', flexShrink: 0 }}>
          <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#888' }}>上課堂數</div>
            <div style={{ fontSize: 22, fontWeight: 500 }}>{totalLessons}</div>
          </div>
          <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#888' }}>課程收入（估算）</div>
            <div style={{ fontSize: 22, fontWeight: 500 }}>${totalIncome.toLocaleString()}</div>
          </div>
          <div style={{ background: '#e6f4ea', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#888' }}>已收學費</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: '#188038' }}>${collected.toLocaleString()}</div>
          </div>
        </div>

        {/* 學員明細 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 18px 12px' }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: '#aaa', letterSpacing: '0.05em', marginBottom: 8, paddingTop: 4 }}>學員明細（點擊展開上課時間）</div>
          {studentStats.length === 0
            ? <div style={{ fontSize: 13, color: '#aaa', textAlign: 'center', padding: '24px 0' }}>此區間尚無上課紀錄</div>
            : studentStats.map((s, i) => (
              <div key={i} style={{ marginBottom: 6, border: '0.5px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                {/* 學員行 */}
                <div
                  style={{ display: 'flex', alignItems: 'center', padding: '9px 12px', cursor: 'pointer', background: expandedStudent === s.name ? '#f8f8f8' : '#fff' }}
                  onClick={() => setExpandedStudent(expandedStudent === s.name ? null : s.name)}
                >
                  <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{s.name}</span>
                  <span style={{ color: '#888', marginRight: 10, fontSize: 12 }}>{s.used} 堂</span>
                  <span style={{ color: '#aaa', marginRight: 10, fontSize: 11 }}>× ${s.perL.toLocaleString()}</span>
                  <span style={{ fontWeight: 500, fontSize: 13, minWidth: 72, textAlign: 'right' }}>${s.income.toLocaleString()}</span>
                  <span style={{ fontSize: 10, color: '#bbb', marginLeft: 8 }}>{expandedStudent === s.name ? '▲' : '▼'}</span>
                </div>
                {/* 上課時間明細 */}
                {expandedStudent === s.name && (
                  <div style={{ background: '#fafafa', borderTop: '0.5px solid #eee', padding: '8px 12px' }}>
                    {s.lessons.map((l, j) => (
                      <div key={j} style={{ display: 'flex', gap: 12, fontSize: 12, color: '#555', padding: '3px 0', borderBottom: j < s.lessons.length-1 ? '0.5px solid #f0f0f0' : 'none' }}>
                        <span style={{ color: '#888', width: 80 }}>{l.date}</span>
                        <span style={{ width: 44 }}>{l.time || '--'}</span>
                        <span style={{ color: '#aaa', fontSize: 11 }}>第 {j+1} 堂</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          }
          {studentStats.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 4px', fontSize: 13, fontWeight: 500, borderTop: '0.5px solid #ccc', marginTop: 4 }}>
              <span>合計 {studentStats.length} 位學員</span>
              <span>${totalIncome.toLocaleString()}</span>
            </div>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '0.5px solid #eee', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, background: '#222', border: 'none', color: '#fff', cursor: 'pointer' }} onClick={onClose}>關閉</button>
        </div>
      </div>
    </div>
  );
}


// 日曆管理視窗
function CalendarManagerModal({ calendars, enabledCalendars, calendarCoachMap, onSave, onClose }) {
  const [enabled, setEnabled] = useState({ ...enabledCalendars });
  const [coachMap, setCoachMap] = useState({ ...calendarCoachMap });

  function toggle(id) {
    setEnabled(prev => ({ ...prev, [id]: !prev[id] }));
  }

  const enabledCount = Object.values(enabled).filter(Boolean).length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 520, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid #eee', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>日曆管理</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>選擇要讀取的日曆，並設定對應的教練名稱</div>
          </div>
          <button style={{ background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer' }} onClick={onClose}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {calendars.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 13, color: '#aaa' }}>載入日曆清單中...</div>
          )}
          {calendars.map(cal => (
            <div key={cal.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              borderRadius: 8, marginBottom: 6,
              border: `0.5px solid ${enabled[cal.id] ? '#c8e6c9' : '#eee'}`,
              background: enabled[cal.id] ? '#f0faf3' : '#fff',
            }}>
              {/* 開關 */}
              <div
                style={{ width: 17, height: 17, border: '0.5px solid #ccc', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer', background: enabled[cal.id] ? '#188038' : '#fff', borderColor: enabled[cal.id] ? '#188038' : '#ccc' }}
                onClick={() => toggle(cal.id)}
              >
                {enabled[cal.id] && <div style={{ width: 9, height: 6, borderLeft: '2px solid #fff', borderBottom: '2px solid #fff', transform: 'rotate(-45deg) translate(1px,-1px)' }} />}
              </div>
              {/* 日曆顏色點 */}
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: cal.backgroundColor || '#888', flexShrink: 0, border: '1px solid rgba(0,0,0,0.1)' }} />
              {/* 日曆名稱 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cal.summary}</div>
                <div style={{ fontSize: 10, color: '#aaa', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cal.id}</div>
              </div>
              {/* 教練名稱輸入 */}
              <input
                style={{ width: 120, border: '0.5px solid #ccc', borderRadius: 8, padding: '5px 8px', fontSize: 12, flexShrink: 0 }}
                placeholder="教練名稱"
                value={coachMap[cal.id] || ''}
                onChange={e => setCoachMap(prev => ({ ...prev, [cal.id]: e.target.value }))}
                disabled={!enabled[cal.id]}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '0.5px solid #eee', flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: '#888' }}>已選取 {enabledCount} 個日曆</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#666', cursor: 'pointer' }} onClick={onClose}>取消</button>
            <button style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, background: '#188038', border: 'none', color: '#fff', fontWeight: 500, cursor: 'pointer' }} onClick={() => onSave(enabled, coachMap)}>儲存並重新載入</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 學員分類管理視窗
function GroupManagerModal({ groups, students, onSave, onClose }) {
  const [localGroups, setLocalGroups] = useState(
    groups.length > 0 ? groups : []
  );
  const [newGroupName, setNewGroupName] = useState('');

  const GROUP_COLORS = ['#f5f5f5','#e3f2fd','#fce4ec','#e8f5e9','#fff8e1','#ede7f6','#fbe9e7'];

  function addGroup() {
    if (!newGroupName.trim()) return;
    const newGroup = {
      id: `g_${Date.now()}`,
      name: newGroupName.trim(),
      color: GROUP_COLORS[localGroups.length % GROUP_COLORS.length],
      studentIds: [],
    };
    setLocalGroups(prev => [...prev, newGroup]);
    setNewGroupName('');
  }

  function removeGroup(id) {
    setLocalGroups(prev => prev.filter(g => g.id !== id));
  }

  function toggleStudent(groupId, studentId) {
    setLocalGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const ids = g.studentIds || [];
      return {
        ...g,
        studentIds: ids.includes(studentId)
          ? ids.filter(id => id !== studentId)
          : [...ids, studentId],
      };
    }));
  }

  function renameGroup(id, name) {
    setLocalGroups(prev => prev.map(g => g.id === id ? { ...g, name } : g));
  }

  const [expandedGroup, setExpandedGroup] = useState(null);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 480, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid #eee', flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>學員分類管理</span>
          <button style={{ background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer' }} onClick={onClose}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          <p style={{ fontSize: 12, color: '#999', marginBottom: 14 }}>
            建立分類後，點擊分類名稱可以展開選取學員。左側列表中分類可以點擊收合。
          </p>

          {/* 新增分類 */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              style={{ flex: 1, border: '0.5px solid #ccc', borderRadius: 8, padding: '7px 10px', fontSize: 13 }}
              placeholder="新分類名稱（如：新手、體驗課、進階）"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addGroup()}
            />
            <button
              style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, background: '#222', border: 'none', color: '#fff', cursor: 'pointer' }}
              onClick={addGroup}
            >新增</button>
          </div>

          {localGroups.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 13, color: '#aaa' }}>
              尚無分類，輸入名稱後按新增
            </div>
          )}

          {localGroups.map(group => (
            <div key={group.id} style={{ border: '0.5px solid #e0e0e0', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
              {/* 分類標題 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: group.color || '#f5f5f5' }}>
                <input
                  style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, color: '#333' }}
                  value={group.name}
                  onChange={e => renameGroup(group.id, e.target.value)}
                />
                <span style={{ fontSize: 12, color: '#888' }}>
                  {(group.studentIds || []).filter(id => students.find(s => s.id === id && !s.graduated)).length} 位
                </span>
                <button
                  style={{ fontSize: 12, color: '#888', background: 'none', border: '0.5px solid #ccc', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}
                  onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
                >
                  {expandedGroup === group.id ? '收起' : '選取學員'}
                </button>
                <button
                  style={{ fontSize: 14, color: '#e53935', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}
                  onClick={() => removeGroup(group.id)}
                  title="刪除此分類"
                >×</button>
              </div>

              {/* 學員選取 */}
              {expandedGroup === group.id && (() => {
                const inGroupIds = new Set(group.studentIds || []);
                const activeStudentsInGroup = students.filter(s => !s.graduated); // 只顯示未畢業
                const selected = activeStudentsInGroup.filter(s => inGroupIds.has(s.id));
                const notSelected = activeStudentsInGroup.filter(s => !inGroupIds.has(s.id));
                // 已被其他分類選取的學員 ID
                const otherGroupIds = new Set(
                  localGroups.filter(g => g.id !== group.id).flatMap(g => g.studentIds || [])
                );
                return (
                  <div style={{ padding: '10px 12px' }}>
                    {selected.length > 0 && (
                      <>
                        <div style={{ fontSize: 11, color: '#188038', fontWeight: 500, marginBottom: 6 }}>已選取（{selected.length}）</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                          {selected.map(s => (
                            <div key={s.id}
                              style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: '0.5px solid #188038', background: '#e6f4ea', color: '#188038', fontWeight: 500 }}
                              onClick={() => toggleStudent(group.id, s.id)}
                            >
                              {s.name} ✕
                            </div>
                          ))}
                        </div>
                        <div style={{ height: '0.5px', background: '#eee', marginBottom: 10 }} />
                      </>
                    )}
                    <div style={{ fontSize: 11, color: '#aaa', fontWeight: 500, marginBottom: 6 }}>未選取</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {[...notSelected.filter(s => !otherGroupIds.has(s.id)), ...notSelected.filter(s => otherGroupIds.has(s.id))].map(s => (
                        <div key={s.id}
                          style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: `0.5px solid ${otherGroupIds.has(s.id) ? '#e0c84f' : '#ccc'}`, background: otherGroupIds.has(s.id) ? '#fffde7' : '#fff', color: otherGroupIds.has(s.id) ? '#b8860b' : '#555' }}
                          onClick={() => toggleStudent(group.id, s.id)}
                          title={otherGroupIds.has(s.id) ? '此學員已在其他分類中' : ''}
                        >
                          {s.name}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid #eee', flexShrink: 0 }}>
          <button style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#666', cursor: 'pointer' }} onClick={onClose}>取消</button>
          <button style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, background: '#188038', border: 'none', color: '#fff', fontWeight: 500, cursor: 'pointer' }} onClick={() => onSave(localGroups)}>儲存分類</button>
        </div>
      </div>
    </div>
  );
}

// 教練上課詳細視窗
function CoachDetailModal({ events, students, coaches, selectedCoach, getPerLesson, currentMonth, currentYear, calMode, weekOffset, calendarCoachMap, onClose }) {
  const [activeCoach, setActiveCoach] = useState(selectedCoach);
  const now = new Date();

  const weekBase = new Date(now.getTime() + (weekOffset || 0) * 7 * 24 * 3600 * 1000);
  const weekStart = new Date(weekBase); weekStart.setDate(weekBase.getDate() - weekBase.getDay());
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
  const isWeekMode = calMode === 'week';

  function isInPeriod(dateStr) {
    if (!dateStr) return false;
    const p = dateStr.split('/');
    if (isWeekMode) {
      const d = new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]));
      return d >= weekStart && d <= weekEnd;
    }
    return parseInt(p[0]) === currentYear && parseInt(p[1]) === currentMonth;
  }

  function isCoachEvent(ev, c) {
    return (c.calendarId && ev.calendarId === c.calendarId) || ev.colorId === c.colorId;
  }

  const periodLabel = isWeekMode
    ? `${weekStart.getMonth()+1}/${weekStart.getDate()} – ${weekEnd.getMonth()+1}/${weekEnd.getDate()}`
    : `${currentYear}/${currentMonth}`;

  // 教練的課程
  const coachEvents = activeCoach
    ? events.filter(ev => isInPeriod(ev.date) && !ev.isTrial && isCoachEvent(ev, activeCoach))
    : [];

  // 依學員分組統計
  const studentStats = (() => {
    const map = {};
    coachEvents.forEach(ev => {
      if (!map[ev.studentName]) map[ev.studentName] = { name: ev.studentName, count: 0, lessons: [] };
      map[ev.studentName].count++;
      map[ev.studentName].lessons.push({ date: ev.date, time: ev.time });
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  })();

  const totalIncome = studentStats.reduce((sum, s) => {
    const student = students.find(st => st.name === s.name);
    return sum + s.count * (student ? getPerLesson(student) : 0);
  }, 0);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
      <div style={{ background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 520, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid #eee', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>教練上課狀況</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{periodLabel}</div>
          </div>
          <button style={{ background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer' }} onClick={onClose}>×</button>
        </div>

        {/* 教練 Tab */}
        <div style={{ display: 'flex', gap: 6, padding: '10px 18px', borderBottom: '0.5px solid #eee', flexShrink: 0, flexWrap: 'wrap' }}>
          {coaches.map(c => {
            const cnt = events.filter(ev => isInPeriod(ev.date) && !ev.isTrial && isCoachEvent(ev, c)).length;
            const isActive = activeCoach?.colorId === c.colorId;
            return (
              <div key={c.colorId}
                style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', fontWeight: isActive ? 500 : 400, background: isActive ? c.hex : c.hex + '18', color: isActive ? '#fff' : c.hex, border: `0.5px solid ${c.hex}55` }}
                onClick={() => setActiveCoach(c)}
              >
                {c.name} · {cnt} 堂
              </div>
            );
          })}
        </div>

        {/* 內容 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {!activeCoach ? (
            <div style={{ fontSize: 13, color: '#aaa', textAlign: 'center', padding: '24px 0' }}>請選擇教練</div>
          ) : studentStats.length === 0 ? (
            <div style={{ fontSize: 13, color: '#aaa', textAlign: 'center', padding: '24px 0' }}>此期間無上課紀錄</div>
          ) : (
            <>
              {/* 摘要 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
                <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: '#888' }}>總堂數</div>
                  <div style={{ fontSize: 20, fontWeight: 500 }}>{coachEvents.length}</div>
                </div>
                <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: '#888' }}>學員數</div>
                  <div style={{ fontSize: 20, fontWeight: 500 }}>{studentStats.length}</div>
                </div>
                <div style={{ background: '#e6f4ea', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: '#888' }}>課程收入</div>
                  <div style={{ fontSize: 20, fontWeight: 500, color: '#188038' }}>${totalIncome.toLocaleString()}</div>
                </div>
              </div>

              {/* 學員明細 */}
              <div style={{ fontSize: 11, fontWeight: 500, color: '#aaa', letterSpacing: '0.05em', marginBottom: 8 }}>學員明細</div>
              {studentStats.map((s, i) => {
                const student = students.find(st => st.name === s.name);
                const perL = student ? getPerLesson(student) : 0;
                return (
                  <div key={i} style={{ marginBottom: 10, border: '0.5px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#f8f8f8', borderBottom: s.lessons.length > 0 ? '0.5px solid #eee' : 'none' }}>
                      <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{s.name}</span>
                      <span style={{ fontSize: 12, color: '#888', marginRight: 12 }}>{s.count} 堂</span>
                      {perL > 0 && <span style={{ fontSize: 12, color: '#888', marginRight: 12 }}>× ${perL.toLocaleString()}</span>}
                      {perL > 0 && <span style={{ fontSize: 12, fontWeight: 500, color: '#188038' }}>${(s.count * perL).toLocaleString()}</span>}
                    </div>
                    <div style={{ padding: '6px 12px' }}>
                      {s.lessons.sort((a, b) => a.date.localeCompare(b.date)).map((l, j) => (
                        <div key={j} style={{ fontSize: 12, color: '#666', padding: '2px 0', display: 'flex', gap: 12 }}>
                          <span>{l.date}</span>
                          <span style={{ color: '#aaa' }}>{l.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '0.5px solid #eee', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, background: '#222', border: 'none', color: '#fff', cursor: 'pointer' }} onClick={onClose}>關閉</button>
        </div>
      </div>
    </div>
  );
}

// 日期詳細課程視窗（點擊月曆格子後彈出）
function DayDetailModal({ date, events, coachColorMap, calendarCoachMap, calendarList, onClose }) {
  const parts = date.split('/');
  const label = `${parts[1]}月${parts[2]}日`;
  const days = ['日','一','二','三','四','五','六'];
  const d = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
  const dow = days[d.getDay()];

  const sorted = [...events].sort((a, b) => (a.time || '').localeCompare(b.time || ''));

  function getHex(ev) {
    if (ev.calendarId && calendarList) {
      const cal = calendarList.find(c => c.id === ev.calendarId);
      if (cal?.backgroundColor) return cal.backgroundColor;
    }
    if (ev.colorId && COLOR_MAP[ev.colorId]) return COLOR_MAP[ev.colorId].hex;
    return '#aaa';
  }

  function getCoach(ev) {
    if (ev.calendarId && calendarCoachMap?.[ev.calendarId]) return calendarCoachMap[ev.calendarId];
    if (ev.colorId && coachColorMap?.[ev.colorId]) return coachColorMap[ev.colorId];
    return null;
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 600 }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 540, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid #eee', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 500 }}>{label}（{dow}）</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{sorted.length} 堂課</div>
          </div>
          <button style={{ background: 'none', border: 'none', fontSize: 22, color: '#888', cursor: 'pointer', lineHeight: 1 }} onClick={onClose}>×</button>
        </div>

        {/* 課程列表 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 18px' }}>
          {sorted.map((ev, i) => {
            const hex = getHex(ev);
            const coach = getCoach(ev);
            return (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '0.5px solid #f0f0f0', alignItems: 'flex-start' }}>
                <div style={{ width: 3, borderRadius: 2, background: hex, flexShrink: 0, alignSelf: 'stretch', minHeight: 36 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>{ev.studentName}</div>
                  {ev.time && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{ev.time}</div>}
                  {coach && (
                    <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 20, background: hex + '22', color: hex, border: `0.5px solid ${hex}44`, display: 'inline-block', marginTop: 4, fontWeight: 500 }}>
                      {coach}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '0.5px solid #eee', flexShrink: 0 }}>
          <button style={{ width: '100%', padding: '10px', borderRadius: 10, fontSize: 13, background: '#222', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 500 }} onClick={onClose}>關閉</button>
        </div>
      </div>
    </div>
  );
}
